import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  StepEdge,
  SmoothStepEdge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  ThemeProvider,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  setNodes,
  setEdges,
  setFinalWorkflowJson,
  loadWorkflow,
  saveWorkflow,
  setTriggerResponse,
  markWorkflowDirty,
  clearWorkflowDirty,
  fetchWorkflowConfigAndSet,
} from '../state/slices/workflowSlice';
import NodeConfigurationPanel from './NodeConfigurationModal';
import BacktrackConfigurationModal from './BacktrackConfigurationModal';
import { theme } from '../style/theme';
import SourceNode from './customizedNodes/SourceNode';
import DestinationNode from './customizedNodes/DestinationNode';
import TransformationNode from './customizedNodes/TransformationNode';
import ProcessorNode from './customizedNodes/ProcessorNode';
import IntegrationNode from './customizedNodes/IntegrationNode';
import BacktrackNode from './customizedNodes/BacktrackNode';
import FunctionNode from './Component/FunctionNode';
import Sidebar from './SideDrawer';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EntitySelectionDialog from './Component/EntitySelectionDialog';
import SqlHostPortDialog from './Component/SqlHostPortDialog';
import { useThemeContext } from '../Context/ThemeContext';
import {
  removeEntitiesForKey,
  setConfirmedDestinationEntities,
  setConfirmedSourceEntities,
  setSelectedDestinationEntities,
  setSelectedSourceEntities,
} from '../state/slices/entitiesSlice';
import { toast } from 'react-toastify';
import {
  removeFinalConfigById,
  resetMappingForWorkflow,
  setFilterExpressionForWorkflow,
  setFinalConfigPayloadForWorkflow,
  fetchResolvedFields,
  runFieldMappingIfNeeded,
  getSavedMapping,
  applySavedMappingIfNeeded,
  setMappedEntities,
  setUnmappedEntities,
} from '../state/slices/mappingSlice';
import {
  postWorkflowToBackend,
  triggerExecuteWorkflow,
  fetchWorkflowConnections,
} from '../apis/Workflows';
import store from '../state/store';
import ConfirmDialog from './Component/ConfirmDialog';
import { v4 as uuidv4 } from 'uuid';
import { convertOldToNewWorkflow } from '../utils/formatters/workflowFormatter';
import { convertOldMappingsToNew } from '../utils/formatters/convertOldMappingsToNew';
import DecisionConfigurationModal from './DecisionConfigurationModal';
import ProceedMappingDialog from './ProceedMappingDialog';
import LShapeEdge from './LShapeEdge';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoDialog from './Component/InfoDialog';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import BottomConfigurationPanel from './BottomConfigurationPanel';
import salesforceImg from './images/salesicon.png';
import sqlImg from './images/sqlicon1.png';
import oracleImg from './images/oracle1.png';
import { getTemplateById } from '../apis/templateAPI';
import { getModuleName, getDisplayName } from '../utils/connectorUtils';

import RightSideDrawer from './RightSideDrawer';
import SideAwareEdge from './Component/Edges/SideAwareEdge';
import WorkflowActionBar from './WorkflowActionBar';
import { PrimaryTabs } from '../components/ui/tabs';
import ETLMapping from './Component/Mapping';

const sanitizeConnectionPayload = (connection = {}) => {
  const normalizeNullish = (value) =>
    value === undefined || value === null || value === '' ? null : value;

  if (!connection || typeof connection !== 'object') {
    return {
      api_version: null,
      conn_id: null,
      conn_name: '',
    };
  }

  return {
    api_version: normalizeNullish(connection.api_version),
    conn_id: normalizeNullish(
      Object.prototype.hasOwnProperty.call(connection, 'conn_id')
        ? connection.conn_id
        : null,
    ),
    conn_name: Object.prototype.hasOwnProperty.call(connection, 'conn_name')
      ? connection.conn_name
      : '',
  };
};

const sanitizeNodeConfigPayload = (config = {}) => {
  if (!config || typeof config !== 'object') return {};

  const sanitized = Object.entries(config).reduce((acc, [key, value]) => {
    if (key === 'connection') {
      acc.connection = sanitizeConnectionPayload(value);
      return acc;
    }
    if (
      key === 'sqlMetadata' ||
      key === 'connector_name' ||
      key === 'environment'
    ) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  if (config.connection && !sanitized.connection) {
    sanitized.connection = sanitizeConnectionPayload(config.connection);
  }

  return sanitized;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const sanitizeEntityListPayload = (entities = []) =>
  toArray(entities)
    .map((entity) => {
      if (!entity || typeof entity !== 'object') return null;
      const entityName = entity.entity_name || entity.entity || '';
      if (!entityName) return null;
      return {
        entity_name: entityName,
        label: entity.label || entityName,
      };
    })
    .filter(Boolean);

const sanitizeEntityMapPayload = (entityMap = {}) => {
  if (!entityMap || typeof entityMap !== 'object') return {};
  return Object.entries(entityMap).reduce((acc, [key, list]) => {
    acc[key] = sanitizeEntityListPayload(list);
    return acc;
  }, {});
};


const ETLWorkflow = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    nodes = [],
    edges = [],
    workflows = {},
  } = useSelector((state) => state.workflows || {});
  const hasSourceNode = Array.isArray(nodes)
    ? nodes.some((n) => n?.type === 'sourceNode' || n?.type === 'source')
    : false;
  const datasetOptions = [
    { label: 'Salesforce', icon: salesforceImg },
    { label: 'SQL', icon: sqlImg },
    { label: 'Oracle', icon: oracleImg },
  ];

  const categorizedConnectors = {
    saas: {
      title: 'SaaS Connectors',
      items: datasetOptions.filter(item => ['Salesforce'].includes(item.label)),
    },
    database: {
      title: 'Database Connectors',
      items: datasetOptions.filter(item => ['SQL', 'Oracle'].includes(item.label)),
    },
  };
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const { darkMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState(null);
  const [pendingDeleteNodeIds, setPendingDeleteNodeIds] = useState([]);
  const [pendingDeleteNodeLabel, setPendingDeleteNodeLabel] = useState('');
  const [sqlHostPortDialog, setSqlHostPortDialog] = useState(null);

  const [precheckOpen, setPrecheckOpen] = useState(false);
  const [requiredEntityCount, setRequiredEntityCount] = useState(0);
  const [enableBacktrack, setEnableBacktrack] = useState(false);
  const [selectSourceModalOpen, setSelectSourceModalOpen] = useState(false);
  const [datasetSelection, setDatasetSelection] = useState(null);
  const [datasetTarget, setDatasetTarget] = useState(null);
  
  const [currentStep, setCurrentStep] = useState('selectDataset');
  const [mappingData, setMappingData] = useState(null);
  const [showMappingPanel, setShowMappingPanel] = useState(false);
  const [nodeSelectedFromCanvas, setNodeSelectedFromCanvas] = useState(false);
  const [resolvedFields, setResolvedFields] = useState(null);
  const [autoMappedFields, setAutoMappedFields] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 = Workflow, 1 = Mapping
  const resolveDropPosition = useCallback(() => {
    try {
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (reactFlowInstance.current && bounds) {
        const cx = bounds.left + bounds.width / 2;
        const cy = bounds.top + bounds.height / 3;
        return reactFlowInstance.current.screenToFlowPosition({
          x: cx,
          y: cy,
        });
      }
    } catch (err) {
      console.warn('Failed to resolve drop position', err);
    }
    return { x: 0, y: 0 };
  }, []);

  const defaultEdgeOptions = useMemo(
    () => ({
      type: 'smoothstep',
      animated: false,
      style: { stroke: '#3c9362', strokeWidth: 2 },
      markerEnd: { type: 'arrowclosed', width: 12, height: 12, color: '#3c9362' },
    }),
    [],
  );


  useEffect(() => {
    const hasBacktrackNodes = nodes.some((n) => n.type === 'backtrackNode');
    if (hasBacktrackNodes !== enableBacktrack) {
      setEnableBacktrack(hasBacktrackNodes);
    }
  }, [nodes, enableBacktrack]);

  const edgeTypes = {
    lshape: LShapeEdge,
    sideaware: SideAwareEdge,
    step: StepEdge,
    smoothstep: SmoothStepEdge,
  };

  const handleOpenSelectSourceModal = (event) => {
    event?.stopPropagation();
    setDatasetTarget(null);
    setDatasetSelection(null);
    setSelectSourceModalOpen(true);
  };

  console.log('nodes::', nodes);

  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [mappingDialogData, setMappingDialogData] = useState({
    source: '',
    dest1: '',
    dest2: '',
  });
  const handleCloseMapping = () => setMappingDialogOpen(false);

  const { workflowName: workflowId } = useParams();


  const [workflowname, setWorkflowName] = useState(workflowId);


  const [collapsedBoth, setCollapsedBoth] = useState(false);

  const workflowData = workflows?.[workflowId];
  const unsavedFlag = useSelector(
    (state) => state.workflows?.unsavedChangesByWorkflow?.[workflowId],
  );

  console.log('workflowData data:', workflowData);

  const scheduleDateTime = workflowData?.scheduleDateTime;
  const timeZone = workflowData?.timezone;

  console.log('schedule date:', scheduleDateTime);
  console.log('timeZone:', timeZone);


  const workflowConfigurationId =
    workflows?.[workflowId]?.workflowConfigurationId;
  const workflowRunId = workflows?.[workflowId]?.workflowRunId;

  console.log('workflowData:', workflowData);
  console.log('workflowId:', workflowId);
  console.log('workflowRunId:', workflowRunId);
  console.log('workflowConfigurationId:', workflowConfigurationId);

  const selectedSourceEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const selectedDestinationEntitiesByWorkflow = useSelector(
    (state) =>
      state.entities?.selectedDestinationEntitiesByWorkflow?.[workflowId] || {},
  );
  const confirmedSourceEntities = useSelector(
    (state) =>
      state.entities?.confirmedSourceEntitiesByWorkflow?.[workflowId] || {},
  );

  const confirmedDestinationEntities = useSelector(
    (state) =>
      state.entities?.confirmedDestinationEntitiesByWorkflow?.[workflowId] ||
      {},
  );

  const sanitizedEntitySelections = useMemo(
    () => ({
      source: sanitizeEntityMapPayload(confirmedSourceEntities),
      destination: sanitizeEntityMapPayload(confirmedDestinationEntities),
    }),
    [confirmedSourceEntities, confirmedDestinationEntities],
  );


  const [openDecisionDrawer, setOpenDecisionDrawer] = useState(false);
  const [decisionFields, setDecisionFields] = useState([]);
  const [clearSourceSelection, setClearSourceSelection] = useState(0);

  const sidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const actionRef = useRef(null);
  const dirtySnapshotRef = useRef(null);
  const dirtyInitializedRef = useRef(false);

  const mappingConnectionDetails = useSelector(
    (state) =>
      state.mapping.mappingsByWorkflow?.[workflowId]?.connectionDetails,
  );
  const workflowConnectionDetails = workflows?.[workflowId]?.connectionDetails;

  const connectionDetails =
    mappingConnectionDetails && mappingConnectionDetails.length > 0
      ? mappingConnectionDetails
      : (workflowConnectionDetails ?? []);

  const dirtySnapshot = useMemo(() => {
    try {
      return JSON.stringify({
        nodes,
        edges,
        connectionDetails,
        scheduleDateTime: workflowData?.scheduleDateTime ?? null,
        timezone: workflowData?.timezone ?? null,
        frequency: workflowData?.frequency ?? null,
        endDate: workflowData?.endDate ?? null,
        customDays: workflowData?.customDays ?? null,
        hourInterval: workflowData?.hourInterval ?? null,
      });
    } catch (err) {
      console.warn('Failed to compute dirty snapshot:', err);
      return null;
    }
  }, [
    nodes,
    edges,
    connectionDetails,
    workflowData?.scheduleDateTime,
    workflowData?.timezone,
    workflowData?.frequency,
    workflowData?.endDate,
    workflowData?.customDays,
    workflowData?.hourInterval,
  ]);

  console.log('connectionDetails:', connectionDetails);

  useEffect(() => {
    dirtySnapshotRef.current = null;
    dirtyInitializedRef.current = false;
  }, [workflowId]);

  useEffect(() => {
    if (!workflowId || !dirtySnapshot) return;

    if (dirtySnapshotRef.current === null) {
      dirtySnapshotRef.current = dirtySnapshot;
      return;
    }

    if (!dirtyInitializedRef.current && unsavedFlag === false) {
      dirtySnapshotRef.current = dirtySnapshot;
      dirtyInitializedRef.current = true;
      return;
    }

    dirtyInitializedRef.current = true;

    if (dirtySnapshot !== dirtySnapshotRef.current) {
      dirtySnapshotRef.current = dirtySnapshot;
      dispatch(markWorkflowDirty({ workflowId }));
    }
  }, [dirtySnapshot, workflowId, dispatch, unsavedFlag  ]);

  const nodesKey = useMemo(() => {
    if (!nodes || nodes.length === 0) return 'empty';
    return nodes
      .map((n) => `${n.id}:${n.type}:${n.data?.config?.connection?.conn_id || ''}:${n.data?.config?.module || ''}`)
      .join('|');
  }, [nodes]);

  const edgesKey = useMemo(() => {
    if (!edges || edges.length === 0) return 'empty';
    return edges
      .map((e) => `${e.id}:${e.source}:${e.target}`)
      .join('|');
  }, [edges]);

  const processWorkflowData = () => {
    const formattedNodes = nodes.map((node) => {
      const match = node.data.label.match(/(.*?)\s*\((.*?)\)/);
      const label = match ? match[1].trim() : node.data.label;
      const type = match ? match[2].trim().toLowerCase() : node.type;

      return {
        id: node.id,
        position: node.position,
        label,
        type,
        config:
          typeof node.data.config === 'object' ? { ...node.data.config } : {},
      };
    });

    const formattedEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      animated: edge.animated,
      label: edge.label || '',
      markerEnd: {
        type: 'arrowclosed',
        width: 20,
        height: 20,
        color: darkMode ? '#3a8eff' : '#1976d2',
      },
    }));

    return { nodes: formattedNodes, edges: formattedEdges };
  };

  const processWorkflowDatas = useMemo(
    () => processWorkflowData(),
    [nodesKey, edgesKey, darkMode],
  );



  const WorkflowSaveJson = () => {
    const toTitle = (value = '') => {
      if (typeof value !== 'string') return '';
      const trimmed = value.trim();
      if (!trimmed) return '';
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    const nodeTypeLabel = (type = '') => {
      if (!type) return '';
      const clean = type.replace(/Node$/i, '');
      if (!clean) return '';
      return clean.charAt(0).toUpperCase() + clean.slice(1);
    };

    const deriveLabel = (node) => {
      const dataLabel =
        typeof node?.data?.label === 'string' ? node.data.label.trim() : '';
      const configLabel =
        typeof node?.data?.config?.label === 'string'
          ? node.data.config.label.trim()
          : '';
      const fallbackLabel =
        typeof node?.label === 'string' ? node.label.trim() : '';

      if (dataLabel) return dataLabel;
      if (configLabel) return configLabel;
      if (fallbackLabel) return fallbackLabel;

      const connection = node?.data?.config?.connection || {};
      const connectorRaw =
        connection.connector_name ||
        connection.connector ||
        connection.type ||
        '';
      const connector = toTitle(connectorRaw) || 'Connector';
      const suffix = nodeTypeLabel(node?.type);
      return suffix ? `${connector} (${suffix})` : connector;
    };

    const formattedNodes = nodes.map((node) => {
      const config =
        node?.data && typeof node.data.config === 'object'
          ? { ...node.data.config }
          : {};
      const label = deriveLabel(node);

      if (!config.label) {
        config.label = label;
      }

      const connectorRaw =
        config?.connection?.connector_name ||
        config?.connection?.connector ||
        config?.connection?.type ||
        '';
      const moduleRaw = typeof config.module === 'string' ? config.module : '';
      const labelContainsSql =
        typeof label === 'string' && label.toLowerCase().includes('sql');
      const isSqlConnector =
        connectorRaw?.toLowerCase() === 'sql' ||
        moduleRaw?.toLowerCase() === 'sql' ||
        labelContainsSql;

      if (!config.id) {
        config.id = node.id;
      }
      if (!config.type) {
        config.type = node.type;
      }

      if (isSqlConnector) {
        const normalizedModule = moduleRaw ? moduleRaw.toLowerCase() : 'sql';
        config.module = normalizedModule;
      }

      const sanitizedConfig = sanitizeNodeConfigPayload(config);
      const orderedConfig = {
        ...(sanitizedConfig.id !== undefined && { id: sanitizedConfig.id }),
        ...(sanitizedConfig.label !== undefined && { label: sanitizedConfig.label }),
        ...(sanitizedConfig.type !== undefined && { type: sanitizedConfig.type }),
        ...(sanitizedConfig.connection !== undefined && { connection: sanitizedConfig.connection }),
        ...(sanitizedConfig.module !== undefined && { module: sanitizedConfig.module }),
      };
      Object.keys(sanitizedConfig).forEach(key => {
        if (!['id', 'label', 'type', 'connection', 'module'].includes(key)) {
          orderedConfig[key] = sanitizedConfig[key];
        }
      });

      return {
        id: node.id,
        label,
        type: node.type,
        position: node.position,
        config: orderedConfig,
      };
    });


    const formattedEdges = edges.map((edge) => {
      let edgeId = edge.id;
      if (edgeId && edgeId.startsWith('xy-edge__')) {
        edgeId = `e-${edgeId.replace('xy-edge__', '')}`;
      } else if (edgeId && !edgeId.startsWith('e-')) {
        edgeId = `e-${edgeId}`;
      }
      
      const edgeType = edge.type === 'smoothstep' ? 'sideaware' : (edge.type || 'sideaware');
      
      const markerEnd = edge.markerEnd ? {
        ...edge.markerEnd,
        width: 20,
        height: 20,
      } : {
        type: 'arrowclosed',
        width: 20,
        height: 20,
        color: '#1976d2',
      };
      
      return {
        id: edgeId,
        source: edge.source,
        target: edge.target,
        type: edgeType,
        animated: true,
        markerEnd,
      };
    });

    const GenericTransform = nodes
      .filter((node) => node.type === 'transformationNode')
      .map((node) => node.data?.config?.transform || {})
      .filter((t) => Object.keys(t).length > 0);

    return {
      nodes: formattedNodes,
      edges: formattedEdges,
      GenericTransform,
      mapping: connectionDetails,
      entitySelections: sanitizedEntitySelections,
    };
  };

  const saveJson = useMemo(
    () => WorkflowSaveJson(),
    [
      JSON.stringify(nodes),
      JSON.stringify(edges),
      JSON.stringify(connectionDetails),
    ],
  );

  const { nodes: formattedNodes, edges: formattedEdges } =
    convertOldToNewWorkflow(saveJson);


  const filterExpression = useSelector(
    (state) => state.mapping?.filterExpressionByWorkflow?.[workflowId],
  );

  useEffect(() => {
    if (filterExpression) return;
    try {
      const raw = localStorage.getItem(`workflow_filters:${workflowname}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          parsed.filterGroup &&
          Array.isArray(parsed.filterGroup.conditions)
        ) {
          dispatch(
            setFilterExpressionForWorkflow({
              workflowName: workflowname,
              filterExpression: parsed,
            }),
          );
        }
      }
    } catch (e) {
      console.log(e);
    }
  }, [filterExpression, workflowname, dispatch]);

  const formattedNodesWithFilter = useMemo(() => {
    try {
      return formattedNodes.map((n) => {
        if (n.type === 'sourceNode' || n.type === 'source') {
          const existingConfig = n?.data?.config || {};
          const nextConfig = { ...existingConfig };

          if (filterExpression) {
            const group = filterExpression?.filterGroup
              ? filterExpression.filterGroup
              : filterExpression;

            if (Array.isArray(group?.conditions)) {
              nextConfig.filterExpression = {
                filterGroup: {
                  ...(group.conditions.length > 1
                    ? {
                        condition_operator:
                          group.condition_operator || group.logic || 'AND',
                      }
                    : {}),
                  conditions: group.conditions.map((c) => ({
                    entity_name: c.entity_name,
                    source_field: c.source_field,
                    operator: c.operator,
                    input_value: c.input_value,
                    ...(c.connection_scope
                      ? { connection_scope: c.connection_scope }
                      : {}),
                  })),
                },
              };
            } else if (
              group?.entity_name &&
              group?.source_field &&
              group?.operator !== undefined
            ) {
              nextConfig.filterExpression = {
                filterGroup: {
                  conditions: [
                    {
                      entity_name: group.entity_name,
                      source_field: group.source_field,
                      operator: group.operator,
                      input_value: group.input_value,
                      ...(group.connection_scope
                        ? { connection_scope: group.connection_scope }
                        : {}),
                    },
                  ],
                },
              };
            }
          } else {
            if ('filterExpression' in nextConfig) {
              delete nextConfig.filterExpression;
            }
          }

          return {
            ...n,
            data: {
              ...n.data,
              config: nextConfig,
            },
          };
        }
        return n;
      });
    } catch (e) {
      return formattedNodes;
    }
  }, [formattedNodes, filterExpression]);

  console.log('formattedNodes:::', formattedNodesWithFilter);


  useEffect(() => {
    if (filterExpression) return;
    try {
      const srcNode = formattedNodesWithFilter.find(
        (n) =>
          (n.type === 'sourceNode' || n.type === 'source') &&
          n?.data?.config?.filterExpression?.filterGroup &&
          Array.isArray(
            n.data.config.filterExpression.filterGroup.conditions,
          ) &&
          n.data.config.filterExpression.filterGroup.conditions.length > 0,
      );
      if (srcNode) {
        const group = srcNode.data.config.filterExpression.filterGroup;
        dispatch(
          setFilterExpressionForWorkflow({
            workflowName: workflowname,
            filterExpression: { filterGroup: group },
          }),
        );
      }
    } catch (e) {
      console.log('Error setting filter expression from source node:', e);
    }
  }, [filterExpression, formattedNodesWithFilter, workflowname, dispatch]);

  const nodesById = Object.fromEntries(
    formattedNodesWithFilter.map((n) => [
      n.id,
      n.config ? { ...n, config: n.config } : n,
    ]),
  );

  console.log('nodesById:::', nodesById);

  const mappings = convertOldMappingsToNew(saveJson.mapping, nodesById);

  console.log('mappingss:', mappings);








  const handleRequestDeleteNode = (nodeId, nodeLabel) => {
    setPendingDeleteNodeId(nodeId);
    setPendingDeleteNodeLabel(nodeLabel);
    setConfirmDialogOpen(true);
  };


  useEffect(() => {
    const hasBacktrackNodes = nodes.some((n) => n.type === 'backtrackNode');
    if (!hasBacktrackNodes && enableBacktrack) {


      const loadedWorkflow = workflows[workflowId];
      const wasFromBackend =
        loadedWorkflow?.workflowJson?.featureFlags?.enableBacktrack ||
        loadedWorkflow?.config?.featureFlags?.enableBacktrack ||
        loadedWorkflow?.featureFlags?.enableBacktrack;

      if (!wasFromBackend) {
        setEnableBacktrack(false);
      }
    }
  }, [nodes, enableBacktrack, workflows, workflowId]);

  const nodeTypes = {
    sourceNode: (props) => (
      <SourceNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    destinationNode: (props) => (
      <DestinationNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    transformationNode: (props) => (
      <TransformationNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    processorNode: (props) => (
      <ProcessorNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    integrationNode: (props) => (
      <IntegrationNode
        {...props}
        deleteNode={handleRequestDeleteNode}
        updatedConfig={processWorkflowDatas}
        onEntitySelectionComplete={handleEntitySelectionComplete}
      />
    ),
    backtrackNode: (props) => (
      <BacktrackNode {...props} deleteNode={handleRequestDeleteNode} />
    ),
    functionNode: (props) => (
      <FunctionNode {...props} updatedConfig={processWorkflowDatas} />
    ),
  };

  const cleanLabel = (s) => String(s || '').replace(/\s*\(.*?\)\s*$/, '');


  const nodeConnectionName = (n) => {
    const conn = n?.data?.config?.connection || n?.config?.connection;
    return conn?.conn_name || conn?.name || null;
  };

  const nodeDisplayName = (n) => {
    if (!n) return '';
    if (n.label) return cleanLabel(n.label);
    if (n.data?.label) return cleanLabel(n.data.label);
    if (n.data?.config?.label) return cleanLabel(n.data.config.label);
    return n.id || '';
  };

  const isDestNode = (n) =>
    n?.type === 'destination' ||
    n?.type === 'destinationNode' ||
    n?.config?.type === 'destinationNode';
  const isSourceNode = (n) =>
    n?.type === 'source' ||
    n?.type === 'sourceNode' ||
    n?.config?.type === 'sourceNode';
  const findDecisionNode = (nodesArr) =>
    nodesArr.find(
      (n) =>
        String(n.id || '').startsWith('decision-') || !!n?.config?.decision,
    ) || null;





































  const getMappingEndpoints = (decisionNode, nodes, edges) => {
    if (!decisionNode)
      return {
        sourceId: null,
        dest1Id: null,
        dest2Id: null,
        source: '',
        dest1: '',
        dest2: '',
      };

    const inEdges = edges.filter((e) => e.target === decisionNode.id);




    const outEdges = edges.filter((e) => e.source === decisionNode.id);





    const sourceNode = nodes.find(
      (n) => isSourceNode(n) && inEdges.some((e) => e.source === n.id),
    );
    const destNodes = outEdges
      .map((e) => nodes.find((n) => n.id === e.target))
      .filter(isDestNode);

    const destIds = destNodes.map((d) => d.id);
    const destNames = destNodes.map((d) => nodeDisplayName(d));
    const sourceName =
      nodeConnectionName(sourceNode) || nodeDisplayName(sourceNode);
    return {
      decisionNodeId: decisionNode.id,
      sourceId: sourceNode?.id || null,
      dest1Id: destNodes[0]?.id || null,
      dest2Id: destNodes[1]?.id || null,
      destIds,
      destNames,
      source: sourceName,
      dest1: destNames[0] || '',
      dest2: destNames[1] || '',
    };
  };

















































































































  const updateNodesWithFields = useCallback((resolvedFieldsData, autoMappedFieldsData, savedMappingsData = []) => {
    if (!resolvedFieldsData || !Array.isArray(resolvedFieldsData)) return;

    const updatedNodes = nodes.map((node) => {
      const nodeId = node.id;
      const isSource = node.type === 'sourceNode' || node.type === 'source';
      const isDestination = node.type === 'destinationNode' || node.type === 'destination';

      if (!isSource && !isDestination) {
        return node;
      }

      const nodeFields = resolvedFieldsData
        .filter((rf) => {
          const nodeConnId = node.data?.config?.connection?.conn_id;
          const rfConnId = rf?.connection?.conn_id;
          const matchesType = (isSource && rf.type === 'source') || (isDestination && rf.type === 'destination');
          
          if (rf.nodeId === nodeId) return matchesType;
          
          if (nodeConnId && rfConnId && nodeConnId === rfConnId) return matchesType;
          
          return false;
        })
        .flatMap((rf) => rf.fields || [])
        .map((field) => {
          if (typeof field === 'string') return field;
          return field?.name || field?.field_name || field?.fieldName || String(field);
        });

      if (nodeFields.length === 0) {
        return node;
      }

      return {
        ...node,
        data: {
          ...node.data,
          fields: nodeFields,
          autoMappedFields: autoMappedFieldsData,
          savedMappings: savedMappingsData,
        },
      };
    });

    const hasChanges = JSON.stringify(updatedNodes) !== JSON.stringify(nodes);
    if (hasChanges) {
      dispatch(setNodes(updatedNodes));
    }
  }, [nodes, dispatch]);

  const handleEntitySelectionComplete = async (data) => {
    console.log('=== handleEntitySelectionComplete called ===');
    console.log('Received data:', data);
    
    const {
      finalConfigPayload,
      selectedSourceEntities,
      selectedDestinationEntities,
      decisionMeta,
      secondaryDestinationId,
      shouldSkipFuzzy,
      primaryDestinationId,
      sourceName,
      dest1Name,
      dest2Name,
    } = data || {};

    console.log('finalConfigPayload:', finalConfigPayload);
    console.log('finalConfigPayload length:', finalConfigPayload?.length);
    console.log('workflowId:', workflowId);

    if (!finalConfigPayload?.length || !workflowId) {
      console.error('Invalid configuration payload or workflowId missing');
      toast.error('Invalid configuration payload');
      return;
    }

    toast.info('Initializing mapping...', { autoClose: 1000 });

    try {
      dispatch(
        setFinalConfigPayloadForWorkflow({ workflowId, finalConfigPayload })
      );

      if (finalConfigPayload && Array.isArray(finalConfigPayload)) {
        finalConfigPayload.forEach((config) => {
          if (!config || !config.id) return;
          
          const nodeId = config.id;
          const connId = config.connection?.conn_id || '';
          const module = config.module || '';
          
          // Check if it's a SQL connector (by node ID or connector name)
          const isSqlConnector = nodeId?.toLowerCase().startsWith('sql') || 
                                config.connection?.connector_name?.toLowerCase() === 'sql' ||
                                config.connection?.connector?.toLowerCase() === 'sql';
          
          // Normalize module: for SQL, normalize to lowercase; for others, use as-is
          // Only default to 'sql' if it's a SQL connector and module is empty
          let normalizedModule = module;
          if (isSqlConnector) {
            if (!module) {
              normalizedModule = 'sql'; // Default for SQL when empty
            } else if (module.toLowerCase() === 'sql') {
              normalizedModule = 'sql'; // Normalize SQL to lowercase
            }
          }
          // For all other connectors, use the module value as-is (preserves case like "Salesforce Financial Services Cloud")
          
          const key = `${nodeId}-${connId}-${normalizedModule}`;
          
          const selectedEntities = config.selectedEntities || [];
          
          if (config.type === 'source' && selectedEntities.length > 0) {
            console.log(`Storing source entities for key: ${key}`, selectedEntities);
            dispatch(
              setConfirmedSourceEntities({
                workflowId,
                key,
                entities: selectedEntities,
              })
            );
            dispatch(
              setSelectedSourceEntities({
                workflowId,
                key,
                entities: selectedEntities,
              })
            );
          } else if (config.type === 'destination' && selectedEntities.length > 0) {
            console.log(`Storing destination entities for key: ${key}`, selectedEntities);
            dispatch(
              setConfirmedDestinationEntities({
                workflowId,
                key,
                entities: selectedEntities,
              })
            );
            dispatch(
              setSelectedDestinationEntities({
                workflowId,
                key,
                entities: selectedEntities,
              })
            );
          }
        });
      }

      console.log('Calling fetchResolvedFields with:', {
        label: 'FieldResolution',
        workflowId,
        finalConfigPayload,
      });

      const resolvedRes = await dispatch(
        fetchResolvedFields({
          label: 'FieldResolution',
          workflowId,
          finalConfigPayload,
        })
      ).unwrap();
      
      console.log('fetchResolvedFields response:', resolvedRes);

      const resolvedData = resolvedRes?.result || [];
      console.log('ETLWorkflow - fetchResolvedFields result:', resolvedRes);
      console.log('ETLWorkflow - resolvedData:', resolvedData);
      console.log('ETLWorkflow - resolvedData length:', resolvedData.length);
      console.log('ETLWorkflow - resolvedData structure:', JSON.stringify(resolvedData, null, 2));
      
      if (!resolvedData || resolvedData.length === 0) {
        console.error('WARNING: fetchResolvedFields returned empty data!');
        console.error('resolvedRes:', resolvedRes);
        toast.error('No fields were resolved. Please check your entity selection and connection configuration.');
      }
      
      setResolvedFields(resolvedData);
      console.log('ETLWorkflow - setResolvedFields called with:', resolvedData);

      const autoFuzzyEnabled = true;
      const shouldAttemptAutoMap =
        autoFuzzyEnabled && resolvedData.length > 0 && !shouldSkipFuzzy;

      let fuzzyResult = null;
      if (shouldAttemptAutoMap) {
        try {
          console.log('🚀 ETLWorkflow: Calling runFieldMappingIfNeeded');
          console.log('resolvedData length:', resolvedData.length);
          toast.info('Running auto-fuzzy mapping...', { autoClose: 1500 });
          
          // IMPORTANT: Wait for nodes to be created by fetchResolvedFields
          // fetchResolvedFields creates nodes in its fulfilled reducer, but Redux state
          // might not be updated immediately. Wait for nodes to exist.
          let retryCount = 0;
          let fieldMapRes = null;
          while (retryCount < 5) {
            const currentState = store.getState();
            const nodes = currentState.mapping?.mappingsByWorkflow?.[workflowId]?.nodes || [];
            
            if (nodes.length > 0 || retryCount === 4) {
              // Nodes exist or we've tried enough times
              console.log(`📦 Nodes found: ${nodes.length}, calling runFieldMappingIfNeeded (attempt ${retryCount + 1})`);
              fieldMapRes = await dispatch(
                runFieldMappingIfNeeded({
                  workflowId,
                  fields: resolvedData,
                  threshold: 60,
                  fuzzy_scope: 'e',
                  force: retryCount > 0, // Force if we had to wait
                })
              ).unwrap();
              break;
            }
            
            console.log(`⏳ Waiting for nodes to be created... (attempt ${retryCount + 1}/5)`);
            await new Promise(resolve => setTimeout(resolve, 200));
            retryCount++;
          }

          console.log('📊 runFieldMappingIfNeeded result:', fieldMapRes);

          if (!fieldMapRes?.alreadyRun && fieldMapRes?.result) {
            fuzzyResult = fieldMapRes.result;
            const mappedFields = fieldMapRes.result?.mapped_fields || {};
            setAutoMappedFields(mappedFields);

            dispatch(
              setMappedEntities({
                workflowId,
                mappedEntities: {
                  Source: Object.keys(fieldMapRes.result?.mapped_entities || {}),
                  Destination: Object.values(fieldMapRes.result?.mapped_entities || {}),
                },
              })
            );

            dispatch(
              setUnmappedEntities({
                workflowId,
                unmappedEntities: {
                  Source: fieldMapRes.result?.unmapped_entities?.Source || [],
                  Destination: fieldMapRes.result?.unmapped_entities?.Destination || [],
                },
              })
            );
          }
        } catch (fuzzyErr) {
          console.error('Auto fuzzy mapping failed:', fuzzyErr);
          toast.warning('Auto-fuzzy mapping failed, but you can continue with manual mapping', {
            autoClose: 3000,
          });
        }
      }

      let savedMappings = null;
      try {
        const savedRes = await dispatch(getSavedMapping(workflowId)).unwrap();
        savedMappings = savedRes?.data?.mappings || null;
      } catch (savedErr) {
        console.warn('No saved mapping found:', savedErr);
      }

      if (savedMappings && Array.isArray(savedMappings) && savedMappings.length > 0) {
        const isOneToMany = !!(decisionMeta?.destIds?.length > 1 || secondaryDestinationId);
        await dispatch(
          applySavedMappingIfNeeded({
            workflowId,
            mappings: savedMappings,
            isOneToMany,
          })
        );
      }

      setMappingData({
        resolvedFields: resolvedData,
        fuzzyResult,
        savedMappings,
        selectedSourceEntities,
        selectedDestinationEntities,
        decisionMeta,
        secondaryDestinationId,
        primaryDestinationId,
        sourceName,
        dest1Name,
        dest2Name,
      });

      setCurrentStep('mapping');
      setShowMappingPanel(true);

      const autoMappedFieldsForNodes = fuzzyResult?.mapped_fields || {};
      
      let savedMappingsForNodes = [];
      try {
        const savedRes = await dispatch(getSavedMapping(workflowId)).unwrap();
        savedMappingsForNodes = savedRes?.data?.mappings || [];
      } catch (err) {
        console.warn('Could not load saved mappings for node highlighting:', err);
      }
      
      updateNodesWithFields(resolvedData, autoMappedFieldsForNodes, savedMappingsForNodes);

      toast.success('Fields resolved and mapping ready', { autoClose: 2000 });
    } catch (error) {
      console.error('Mapping flow error:', error);
      
      let errorMessage = 'Failed to initialize mapping. Please try again.';
      if (error?.isTimeout) {
        errorMessage = 'Request timeout. The server is taking too long to respond. Please try again.';
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, { autoClose: 5000 });
    }
  };

  useEffect(() => {
    if (selectedNode) {
      // Only auto-advance to configureDataset if we're on selectDataset step
      // AND the node was NOT selected from canvas (canvas clicks should open modal, not advance step)
      if (currentStep === 'selectDataset' && !nodeSelectedFromCanvas) {
        setCurrentStep('configureDataset');
        setShowMappingPanel(true);
      }
      // Don't reset nodeSelectedFromCanvas here - let it persist so modal can render
      // It will be reset when modal closes or when node is deselected
    }
  }, [selectedNode, currentStep, nodeSelectedFromCanvas]);

  // Track the highest step reached to allow backward navigation
  const stepOrder = ['selectDataset', 'configureDataset', 'entitySelection', 'mapping'];
  const getStepIndex = (step) => stepOrder.indexOf(step);
  const [highestStepReached, setHighestStepReached] = useState('selectDataset');
  const previousStepRef = useRef('selectDataset');
  const isManualNavigationRef = useRef(false);

  // Handle manual step changes (from stepper clicks)
  const handleStepChange = useCallback((newStep) => {
    const currentStepIndex = getStepIndex(currentStep);
    const newStepIndex = getStepIndex(newStep);
    
    // Check if this is a backward navigation
    if (newStepIndex < currentStepIndex) {
      isManualNavigationRef.current = true;
    }
    
    setCurrentStep(newStep);
    
    // Update highest step if moving forward
    if (newStepIndex > getStepIndex(highestStepReached)) {
      setHighestStepReached(newStep);
    }
    
    // Reset manual navigation flag after a short delay
    setTimeout(() => {
      isManualNavigationRef.current = false;
    }, 100);
  }, [currentStep, highestStepReached]);

  useEffect(() => {
    const sourceNodes = nodes.filter((n) => n.type === 'sourceNode' || n.type === 'source');
    const destNodes = nodes.filter((n) => n.type === 'destinationNode' || n.type === 'destination');
    const hasNodes = sourceNodes.length > 0 && destNodes.length > 0;
    const hasEdges = edges.length > 0;
    const hasResolvedFields = resolvedFields && resolvedFields.length > 0;
    
    const sourceHasConfig = sourceNodes.some((n) => n.data?.config?.connection?.conn_id);
    const destHasConfig = destNodes.some((n) => n.data?.config?.connection?.conn_id);
    const nodesConfigured = sourceHasConfig && destHasConfig;

    const currentStepIndex = getStepIndex(currentStep);
    const previousStepIndex = getStepIndex(previousStepRef.current);
    const highestStepIndex = getStepIndex(highestStepReached);

    // Check if user manually navigated backward
    const isManualBackwardNavigation = currentStepIndex < previousStepIndex || isManualNavigationRef.current;
    
    // Update highest step reached if moving forward
    if (currentStepIndex > highestStepIndex) {
      setHighestStepReached(currentStep);
    }

    // Update previous step ref
    previousStepRef.current = currentStep;

    // If user manually navigated backward or is manually navigating, don't auto-advance
    if (isManualBackwardNavigation || isManualNavigationRef.current) {
      return;
    }

    // Only auto-advance if we're at or before the highest step reached
    const canAutoAdvance = currentStepIndex <= highestStepIndex;

    if (!canAutoAdvance) {
      return;
    }

    if (hasResolvedFields && currentStep !== 'mapping' && currentStep !== 'entitySelection') {
      setCurrentStep('mapping');
      setShowMappingPanel(true);
      setHighestStepReached('mapping');
    } else if (nodesConfigured && hasEdges && currentStep !== 'mapping' && currentStep !== 'entitySelection') {
      setCurrentStep('entitySelection');
      setShowMappingPanel(true);
      setHighestStepReached('entitySelection');
    } else if (hasNodes && hasEdges && currentStep === 'selectDataset') {
      setCurrentStep('configureDataset');
      setShowMappingPanel(true);
      setHighestStepReached('configureDataset');
    } else if (hasNodes && !hasEdges && currentStep === 'selectDataset') {
      setShowMappingPanel(true);
    }
  }, [nodes, edges, resolvedFields, currentStep, highestStepReached]);

  const handleProceedAndNavigate = async (overrideDestId = null) => {
    setMappingDialogOpen(false);

    const updatedConfig = processWorkflowDatas;
    const nodes = updatedConfig?.nodes || [];
    const edges = updatedConfig?.edges || [];

    const { sourceId, dest1Id, source, dest1, dest2 } = mappingDialogData || {};

    const allConfigs =
      nodes
        ?.map((node) => {
          const connId = node.config?.connection?.conn_id || '';
          const mod = node.config?.module || '';
          const nodeKey = `${node.id}-${connId}-${mod}`;
          const isSource = node.type === 'sourceNode' || node.type === 'source';
          const isDestination =
            node.type === 'destinationNode' || node.type === 'destination';
          if (!(isSource || isDestination)) return null;
          return {
            id: node.id,
            label: node.config?.label,
            connection: node.config?.connection,
            module: node.config?.module,
            selectedEntities: isSource
              ? selectedSourceEntitiesByWorkflow[nodeKey] || []
              : selectedDestinationEntitiesByWorkflow[nodeKey] || [],
            type: isSource ? 'source' : 'destination',
          };
        })
        .filter(Boolean) || [];

    const resolvedSourceId =
      sourceId || allConfigs.find((c) => c.type === 'source')?.id || null;


    const decisionNode = findDecisionNode(nodes);
    let decisionMeta = null;
    let nextDestId = null;
    let destIds = [];
    let destNames = [];

    if (decisionNode) {
      const destNodes = edges
        .filter((e) => e.source === decisionNode.id)
        .map((e) => nodes.find((n) => n.id === e.target))
        .filter(isDestNode);

      destIds = destNodes.map((d) => d.id);
      destNames = destNodes.map((d) => d?.config?.label || d?.label || '');
    }


    const targetDestId =
      typeof overrideDestId === 'number'
        ? (destIds[overrideDestId] ?? dest1Id ?? destIds[0] ?? null)
        : typeof overrideDestId === 'string'
          ? overrideDestId
          : (dest1Id ?? destIds[0] ?? null);

    const filtered = allConfigs.filter((c) => {
      if (c.type === 'source')
        return resolvedSourceId ? c.id === resolvedSourceId : true;
      if (c.type === 'destination') return c.id === targetDestId;
      return false;
    });

    const srcEntities = filtered
      .filter((c) => c.type === 'source')
      .flatMap((c) => c.selectedEntities || []);
    const dstEntities = filtered
      .filter((c) => c.type === 'destination')
      .flatMap((c) => c.selectedEntities || []);


    if (decisionNode && destIds.length) {
      const baseDest = targetDestId;
      const currentIndex = Math.max(0, destIds.indexOf(baseDest));
      nextDestId = destIds[currentIndex + 1] ?? null;

      decisionMeta = {
        decisionNodeId: decisionNode.id,
        sourceId: resolvedSourceId,
        destIds,
        destNames,
        currentIndex,
      };
    } else {

      const destNodes = nodes.filter(isDestNode);
      if (destNodes.length > 1) {
        const dIds = destNodes.map((d) => d.id);
        const dNames = destNodes.map((d) => d?.config?.label || d?.label || '');
        decisionMeta = {
          decisionNodeId: decisionNode?.id || null,
          sourceId: resolvedSourceId,
          destIds: dIds,
          destNames: dNames,
          currentIndex: Math.max(0, dIds.indexOf(targetDestId)),
        };
        nextDestId = dIds[decisionMeta.currentIndex + 1] || null;
      }
    }

    const canGoNext = !!nextDestId;

    handleEntitySelectionComplete({
      finalConfigPayload: filtered,
      selectedSourceEntities: srcEntities,
      selectedDestinationEntities: dstEntities,
      canGoNext,
      primaryDestinationId: targetDestId,
      secondaryDestinationId: nextDestId,
      decisionMeta,
      sourceName: source,
      dest1Name: dest1,
      dest2Name: dest2,
    });
  };

  useEffect(() => {
    const st = location.state || {};
    const proceed = st.proceedNextMapping || st.proceedSecondMapping;
    let meta = st.decisionMeta;
    const legacyNext = st.secondaryDestinationId;

    if (!proceed) return;

    if (!meta || !Array.isArray(meta.destIds) || meta.destIds.length === 0) {
      const nodes = processWorkflowDatas?.nodes || [];
      const edges = processWorkflowDatas?.edges || [];
      const decisionNode = findDecisionNode(nodes);
      if (!decisionNode) return;
      if (decisionNode) {
        const destNodes = edges
          .filter((e) => e.source === decisionNode.id)
          .map((e) => nodes.find((n) => n.id === e.target))
          .filter(isDestNode);
        const destIds = destNodes.map((d) => d.id);
        const destNames = destNodes.map(
          (d) => d?.config?.label || d?.label || '',
        );
        meta = {
          decisionNodeId: decisionNode.id,
          sourceId: nodes.find(isSourceNode)?.id || null,
          destIds,
          destNames,
          currentIndex: Math.max(0, destIds.indexOf(legacyNext)) || 0,
        };
      }
    }

    if (meta?.destIds?.length) {
      const overrideDestId = meta.destIds[meta.currentIndex] || null;
      if (overrideDestId) {
        navigate(location.pathname, { replace: true, state: {} });
        handleProceedAndNavigate(overrideDestId);
      }
    } else if (legacyNext) {
      navigate(location.pathname, { replace: true, state: {} });
      handleProceedAndNavigate(legacyNext);
    }

  }, [location.key]);

  const buildFinalConfigPayloadForVisualBuilder = useCallback((overrideDestId = null) => {
    const allConfigs =
      nodes
        ?.map((node) => {
          const connId = node.data?.config?.connection?.conn_id || '';
          const mod = node.data?.config?.module || '';
          const nodeKey = `${node.id}-${connId}-${mod}`;
          const isSource = node.type === 'sourceNode' || node.type === 'source';
          const isDestination =
            node.type === 'destinationNode' || node.type === 'destination';
          if (!(isSource || isDestination)) return null;
          return {
            id: node.id,
            label: node.data?.config?.label,
            connection: node.data?.config?.connection,
            module: node.data?.config?.module,
            selectedEntities: isSource
              ? selectedSourceEntitiesByWorkflow[nodeKey] || []
              : selectedDestinationEntitiesByWorkflow[nodeKey] || [],
            type: isSource ? 'source' : 'destination',
          };
        })
        .filter(Boolean) || [];

    const resolvedSourceId =
      allConfigs.find((c) => c.type === 'source')?.id || null;

    const decisionNode = findDecisionNode(nodes);
    let decisionMeta = null;
    let nextDestId = null;
    let destIds = [];
    let destNames = [];
    
    // Calculate dest1Id from all destination nodes (fallback for one-to-one scenarios)
    const allDestNodes = nodes.filter(isDestNode);
    const dest1Id = allDestNodes[0]?.id || null;

    if (decisionNode) {
      const destNodes = edges
        .filter((e) => e.source === decisionNode.id)
        .map((e) => nodes.find((n) => n.id === e.target))
        .filter(isDestNode);

      destIds = destNodes.map((d) => d.id);
      destNames = destNodes.map((d) => d?.data?.label || d?.data?.config?.label || '');
    }

    const targetDestId =
      typeof overrideDestId === 'number'
        ? (destIds[overrideDestId] ?? dest1Id ?? destIds[0] ?? null)
        : typeof overrideDestId === 'string'
          ? overrideDestId
          : (dest1Id ?? destIds[0] ?? null);

    const filtered = allConfigs.filter((c) => {
      if (c.type === 'source')
        return resolvedSourceId ? c.id === resolvedSourceId : true;
      if (c.type === 'destination') return c.id === targetDestId;
      return false;
    });

    if (decisionNode && destIds.length) {
      const baseDest = targetDestId;
      const currentIndex = Math.max(0, destIds.indexOf(baseDest));
      nextDestId = destIds[currentIndex + 1] ?? null;

      decisionMeta = {
        decisionNodeId: decisionNode.id,
        sourceId: resolvedSourceId,
        destIds,
        destNames,
        currentIndex,
      };
    } else {
      const destNodes = nodes.filter(isDestNode);
      if (destNodes.length > 1) {
        const dIds = destNodes.map((d) => d.id);
        const dNames = destNodes.map((d) => d?.data?.label || d?.data?.config?.label || '');
        decisionMeta = {
          decisionNodeId: decisionNode?.id || null,
          sourceId: resolvedSourceId,
          destIds: dIds,
          destNames: dNames,
          currentIndex: Math.max(0, dIds.indexOf(targetDestId)),
        };
        nextDestId = dIds[decisionMeta.currentIndex + 1] || null;
      }
    }

    return {
      finalConfigPayload: filtered,
      decisionMeta,
      primaryDestinationId: targetDestId,
      secondaryDestinationId: nextDestId,
    };
  }, [nodes, edges, selectedSourceEntitiesByWorkflow, selectedDestinationEntitiesByWorkflow]);

  const handleTabChange = useCallback((newTabIndex) => {
    setActiveTab(newTabIndex);

    if (newTabIndex === 1) {
      // Switching to Mapping/Visual Builder tab
      setShowMappingPanel(true);
      setCurrentStep('mapping');
      const configData = buildFinalConfigPayloadForVisualBuilder();
      if (configData.finalConfigPayload?.length > 0) {
        dispatch(
          setFinalConfigPayloadForWorkflow({
            workflowId,
            finalConfigPayload: configData.finalConfigPayload,
          })
        );
      }
    } else if (newTabIndex === 0) {
      // Switching back to Workflow tab - reset to appropriate step
      if (currentStep === 'mapping') {
        // Update mappingData with current visual builder state to keep them in sync
        const configData = buildFinalConfigPayloadForVisualBuilder();
        if (configData) {
          setMappingData((prev) => ({
            ...(prev || {}),
            decisionMeta: configData.decisionMeta,
            primaryDestinationId: configData.primaryDestinationId,
            secondaryDestinationId: configData.secondaryDestinationId,
          }));
        }
        
        // If we were on mapping step, go back to entitySelection or configureDataset
        const sourceNodes = nodes.filter((n) => n.type === 'sourceNode' || n.type === 'source');
        const destNodes = nodes.filter((n) => n.type === 'destinationNode' || n.type === 'destination');
        const sourceHasConfig = sourceNodes.some((n) => n.data?.config?.connection?.conn_id);
        const destHasConfig = destNodes.some((n) => n.data?.config?.connection?.conn_id);
        
        if (sourceHasConfig && destHasConfig && edges.length > 0) {
          setCurrentStep('entitySelection');
        } else if (sourceNodes.length > 0 && destNodes.length > 0 && edges.length > 0) {
          setCurrentStep('configureDataset');
        } else {
          setCurrentStep('selectDataset');
        }
        // Ensure mapping panel is shown for workflow configuration
        setShowMappingPanel(true);
      }
    }
  }, [buildFinalConfigPayloadForVisualBuilder, dispatch, workflowId, currentStep, nodes, edges]);

  useEffect(() => {
    if (!workflowId) return;

    dispatch(setNodes([]));
    dispatch(setEdges([]));

    if (workflows[workflowId]) {
      const storedWorkflow = workflows[workflowId];
      if (storedWorkflow.nodes?.length > 0 || storedWorkflow.edges?.length > 0) {
        dispatch(loadWorkflow({ workflowId }));
      }
      return;
    }

    dispatch(fetchWorkflowConfigAndSet({ workflowId, workflowName: workflowId }));
  }, [workflowId, workflows, dispatch]);











  useEffect(() => {
    const loadedWorkflow = workflows[workflowId];

    console.log('loaded workflow:', loadedWorkflow);


    if (loadedWorkflow?.workflowJson?.featureFlags) {
      setEnableBacktrack(
        loadedWorkflow.workflowJson.featureFlags.enableBacktrack || false,
      );
    } else if (loadedWorkflow?.config?.featureFlags) {
      setEnableBacktrack(
        loadedWorkflow.config.featureFlags.enableBacktrack || false,
      );
    } else if (loadedWorkflow?.featureFlags) {
      setEnableBacktrack(loadedWorkflow.featureFlags.enableBacktrack || false);
    }

    if (
      loadedWorkflow?.workflowJson &&
      !nodes.length &&
      !edges.length &&
      (loadedWorkflow.workflowJson.nodes?.length ||
        loadedWorkflow.workflowJson.edges?.length)
    ) {
      console.log('Workflow Restoration Block TRIGGERED::');
      const savedNodes = loadedWorkflow.workflowJson.nodes || [];
      const savedEdges = loadedWorkflow.workflowJson.edges || [];

      if (savedNodes.length) {
        dispatch(setNodes(savedNodes));
      }
      if (savedEdges.length) {
        dispatch(setEdges(savedEdges));
      }
    }
  }, [workflowId, workflows, dispatch]);

  useEffect(() => {
    const templateState = location.state;
    const isFromTemplate = templateState?.fromTemplate;
    const templateId = templateState?.templateId;
    const templateData = templateState?.templateData;

    if (!isFromTemplate || !workflowId || nodes.length > 0) {
      return;
    }

    const initializeFromTemplate = async () => {
      try {
        let sourceConnection = templateState?.sourceConnection;
        let destinationConnection = templateState?.destinationConnection;

        if (!sourceConnection || !destinationConnection) {
          sourceConnection = sourceConnection || workflowData?.source_connection || workflowData?.sourceConnection;
          destinationConnection = destinationConnection || workflowData?.destination_connection || workflowData?.destinationConnection;
        }

        if (!sourceConnection || !destinationConnection) {
          const state = store.getState();
          const workflowList = state.workflows?.list || [];
          const workflowFromList = workflowList.find((w) => w.workflow_id === workflowId);
          if (workflowFromList) {
            sourceConnection = sourceConnection || workflowFromList.source_connection || workflowFromList.sourceConnection;
            destinationConnection = destinationConnection || workflowFromList.destination_connection || workflowFromList.destinationConnection;
          }
        }

        if (!sourceConnection || !destinationConnection) {
          try {
            const connectionsData = await fetchWorkflowConnections(workflowId);
            if (connectionsData?.connections) {
              const sources = connectionsData.connections.sources || [];
              const destinations = connectionsData.connections.destinations || [];
              if (sources.length > 0 && !sourceConnection) {
                sourceConnection = {
                  conn_id: sources[0].conn_id,
                  conn_name: sources[0].conn_name,
                  connector_name: sources[0].connector_name || sources[0].connector,
                };
              }
              if (destinations.length > 0 && !destinationConnection) {
                destinationConnection = {
                  conn_id: destinations[0].conn_id,
                  conn_name: destinations[0].conn_name,
                  connector_name: destinations[0].connector_name || destinations[0].connector,
                };
              }
            }
          } catch (err) {
            console.warn('Failed to fetch workflow connections:', err);
          }
        }

        if (!sourceConnection || !destinationConnection) {
          console.warn('Template workflow missing source or destination connection');
          return;
        }

        let fullTemplateData = templateData;
        if (!fullTemplateData && templateId) {
          try {
            fullTemplateData = await getTemplateById(templateId);
          } catch (err) {
            console.error('Failed to fetch template data:', err);
          }
        }

        const apiTemplateData = fullTemplateData?.apiData || fullTemplateData;
        const defaultEntityMappings = apiTemplateData?.default_entity_mappings || {};
        const defaultFieldMappings = apiTemplateData?.default_field_mappings || [];
        const nodePositions = apiTemplateData?.node_positions || {};

        console.log('Template initialization - Source connector_name:', sourceConnection.connector_name);
        console.log('Template initialization - Destination connector_name:', destinationConnection.connector_name);

        // Use modules from location state if available, otherwise derive from connector_name
        // Only normalize SQL modules to lowercase; preserve original case for others (e.g., "Salesforce Financial Services Cloud")
        let sourceModule = templateState?.sourceModule || getModuleName(sourceConnection.connector_name);
        let destinationModule = templateState?.destinationModule || getModuleName(destinationConnection.connector_name);
        
        // Normalize only SQL modules to lowercase; preserve original case for others
        const sourceConnectorName = sourceConnection?.connector_name || '';
        const isSourceSql = sourceConnectorName.toLowerCase().includes('sql');
        sourceModule = sourceModule 
          ? (isSourceSql ? sourceModule.toLowerCase().trim() : sourceModule.trim())
          : '';
        
        const destConnectorName = destinationConnection?.connector_name || '';
        const isDestSql = destConnectorName.toLowerCase().includes('sql');
        destinationModule = destinationModule 
          ? (isDestSql ? destinationModule.toLowerCase().trim() : destinationModule.trim())
          : '';
        
        // If module is still empty, try to get from connector_name as fallback
        if (!sourceModule && sourceConnection.connector_name) {
          sourceModule = getModuleName(sourceConnection.connector_name);
          // Normalize fallback module if it's SQL
          if (isSourceSql && sourceModule) {
            sourceModule = sourceModule.toLowerCase().trim();
          }
        }
        if (!destinationModule && destinationConnection.connector_name) {
          destinationModule = getModuleName(destinationConnection.connector_name);
          // Normalize fallback module if it's SQL
          if (isDestSql && destinationModule) {
            destinationModule = destinationModule.toLowerCase().trim();
          }
        }

        console.log('Template initialization - Source module:', sourceModule);
        console.log('Template initialization - Destination module:', destinationModule);
        console.log('Template initialization - Source connector_name:', sourceConnection.connector_name);
        console.log('Template initialization - Destination connector_name:', destinationConnection.connector_name);

        const sourceDisplayName = getDisplayName(sourceModule);
        const destinationDisplayName = getDisplayName(destinationModule);

        console.log('Template initialization - Source display name:', sourceDisplayName);
        console.log('Template initialization - Destination display name:', destinationDisplayName);

        const sourceNodeId = `source-${uuidv4()}`;
        const sourceNode = {
          id: sourceNodeId,
          type: 'sourceNode',
          position: nodePositions.source || { x: 100, y: 200 },
          data: {
            label: `${sourceDisplayName} (Source)`,
            config: {
              label: `${sourceDisplayName} (Source)`,
              module: sourceModule,
              connection: {
                conn_id: sourceConnection.conn_id,
                conn_name: sourceConnection.conn_name,
                connector_name: sourceConnection.connector_name,
              },
            },
          },
        };

        const destinationNodeId = `destination-${uuidv4()}`;
        const destinationNode = {
          id: destinationNodeId,
          type: 'destinationNode',
          position: nodePositions.destination || { x: 500, y: 200 },
          data: {
            label: `${destinationDisplayName} (Destination)`,
            config: {
              label: `${destinationDisplayName} (Destination)`,
              module: destinationModule,
              connection: {
                conn_id: destinationConnection.conn_id,
                conn_name: destinationConnection.conn_name,
                connector_name: destinationConnection.connector_name,
              },
            },
          },
        };

        const integrationNodeId = `integration-${uuidv4()}`;
        const integrationNode = {
          id: integrationNodeId,
          type: 'integrationNode',
          position: nodePositions.integration || { x: 300, y: 200 },
          data: {
            label: 'Field Mapping',
            config: {},
          },
        };

        const sourceToIntegrationEdge = {
          id: `e-${sourceNodeId}-${integrationNodeId}`,
          source: sourceNodeId,
          target: integrationNodeId,
          type: 'default',
          animated: true,
        };

        const integrationToDestinationEdge = {
          id: `e-${integrationNodeId}-${destinationNodeId}`,
          source: integrationNodeId,
          target: destinationNodeId,
          type: 'default',
          animated: true,
        };

        dispatch(setNodes([sourceNode, integrationNode, destinationNode]));
        dispatch(setEdges([sourceToIntegrationEdge, integrationToDestinationEdge]));

        if (Object.keys(defaultEntityMappings).length > 0) {
          const sourceNodeKey = `${sourceNodeId}-${sourceConnection.conn_id}-${sourceModule}`;
          const destinationNodeKey = `${destinationNodeId}-${destinationConnection.conn_id}-${destinationModule}`;

          const sourceEntities = Object.keys(defaultEntityMappings).map((entityName) => ({
            entity_name: entityName,
            label: entityName,
          }));

          const destinationEntitiesMap = {};
          Object.values(defaultEntityMappings).forEach((mappings) => {
            if (Array.isArray(mappings)) {
              mappings.forEach((mapping) => {
                const destEntity = mapping.destination_entity || mapping.destinationEntity;
                if (destEntity && !destinationEntitiesMap[destEntity]) {
                  destinationEntitiesMap[destEntity] = {
                    entity_name: destEntity,
                    label: destEntity,
                  };
                }
              });
            }
          });
          const destinationEntities = Object.values(destinationEntitiesMap);

          if (sourceEntities.length > 0) {
            dispatch(
              setSelectedSourceEntities({
                workflowId,
                key: sourceNodeKey,
                entities: sourceEntities,
              }),
            );
            dispatch(
              setConfirmedSourceEntities({
                workflowId,
                key: sourceNodeKey,
                entities: sourceEntities,
              }),
            );
          }

          if (destinationEntities.length > 0) {
            dispatch(
              setSelectedDestinationEntities({
                workflowId,
                key: destinationNodeKey,
                entities: destinationEntities,
              }),
            );
            dispatch(
              setConfirmedDestinationEntities({
                workflowId,
                key: destinationNodeKey,
                entities: destinationEntities,
              }),
            );
          }
        }

        navigate(location.pathname, { replace: true, state: {} });

        console.log('✅ Workflow initialized from template:', {
          sourceNode,
          destinationNode,
          integrationNode,
          entityMappings: defaultEntityMappings,
          fieldMappings: defaultFieldMappings,
        });
      } catch (error) {
        console.error('❌ Error initializing workflow from template:', error);
        toast.error('Failed to initialize workflow from template', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    };

    initializeFromTemplate();
  }, [workflowId, workflowData, nodes.length, location.state, location.pathname, dispatch, navigate]);

  useEffect(() => {
    if (!nodes.length || !edges.length) return;










    const hasConfig = (n) => {
      console.log('hasconfig n:::', n);

      const c = n?.data?.config || {};
      const lbl = (n?.data?.label || '').toLowerCase();

      const isJira = lbl.includes('jira');

      const isSqlLike = lbl.includes('sql') || lbl.includes('postgres');

      if (!c.connection?.conn_id) return false;

      if (isJira) {
        return !!c.project?.id || !!c.project?.key;
      }
      if (isSqlLike) {
        return true;
      }
      return !!c.module;
    };

    const enhancedEdges = edges.map((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);

      const sourceOk = hasConfig(sourceNode);
      const targetOk = hasConfig(targetNode);

      const isIntegrationNode =
        sourceNode?.type === 'integrationNode' ||
        targetNode?.type === 'integrationNode';

      const isJiraLabel =
        (sourceNode?.data?.label || '').toLowerCase().trim() ===
          'jira (integretion)' ||
        (targetNode?.data?.label || '').toLowerCase().trim() ===
          'jira (integretion)';

      const isLShape = isIntegrationNode && isJiraLabel;


      const isBacktrackEdge =
        sourceNode?.type === 'backtrackNode' ||
        targetNode?.type === 'backtrackNode';

      const defaultEdgeColor = '#3c9362';
      const edgeColor = isBacktrackEdge ? '#9c27b6' : defaultEdgeColor;

      return {
        ...edge,

        type: isLShape ? 'lshape' : 'smoothstep',
        data: { sourceOk, targetOk },
        animated: false,
        markerEnd: {
          type: 'arrowclosed',
          width: 12,
          height: 12,
          color: edge.markerEnd?.color || edgeColor,
        },
        style: isBacktrackEdge
          ? { stroke: '#9c27b6', strokeWidth: 2, ...edge.style }
          : { stroke: edgeColor, strokeWidth: 2, ...edge.style },
      };
    });

    if (JSON.stringify(enhancedEdges) !== JSON.stringify(edges)) {
      dispatch(setEdges(enhancedEdges));
    }
  }, [nodes, edges, darkMode, dispatch]);



  const normalizeEntityKey = useCallback((savedKey, nodes, targetType) => {
    if (!savedKey || !nodes || !Array.isArray(nodes)) return savedKey;


    const allNodes = nodes.filter(
      (n) =>
        (targetType === 'source' &&
          (n.type === 'sourceNode' || n.type === 'source')) ||
        (targetType === 'destination' &&
          (n.type === 'destinationNode' || n.type === 'destination')),
    );

    // Handle keys with connector name prefix: "ConnectorName-nodeId-connId-module"
    // or "Select Destination-nodeId-connId-module"
    // Format: {prefix}-{nodeId (UUID)}-{connId (UUID)}-{module}
    const parseKey = (key) => {
      // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (5 parts)
      // Try to find two consecutive UUIDs (nodeId and connId)
      const uuidPattern = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
      const matches = [...key.matchAll(uuidPattern)];
      
      if (matches.length < 2) return null;
      
      // First UUID is nodeId, second is connId
      const nodeId = matches[0][0];
      const connId = matches[1][0];
      
      // Extract prefix (everything before first UUID)
      const prefixEnd = key.indexOf(nodeId);
      const connectorName = key.substring(0, prefixEnd).replace(/-$/, '');
      
      // Extract module (everything after second UUID)
      const moduleStart = key.indexOf(connId) + connId.length + 1;
      const module = key.substring(moduleStart) || '';
      
      return { connectorName, nodeId, connId, module };
    };

    // First, try exact match with current node IDs
    for (const node of allNodes) {
      const nodeConnId = node?.data?.config?.connection?.conn_id || node?.config?.connection?.conn_id || '';
      const nodeId = node?.id || '';
      if (!nodeConnId || !nodeId) continue;

      // Check if it's a SQL connector
      const isSqlConnector = nodeId?.toLowerCase().startsWith('sql') || 
                            node?.data?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                            node?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                            node?.data?.config?.connection?.connector?.toLowerCase() === 'sql' ||
                            node?.config?.connection?.connector?.toLowerCase() === 'sql';

      const nodeModule = node?.data?.config?.module || node?.config?.module || '';
      
      // Normalize module: for SQL, normalize to lowercase; for others, use as-is
      // Only default to 'sql' if it's a SQL connector and module is empty
      let normalizedModule = nodeModule;
      if (isSqlConnector) {
        if (!nodeModule) {
          normalizedModule = 'sql'; // Default for SQL when empty
        } else if (nodeModule.toLowerCase() === 'sql') {
          normalizedModule = 'sql'; // Normalize SQL to lowercase
        }
      }
      // For all other connectors, use the module value as-is (preserves case like "Salesforce Financial Services Cloud")

      const keyWithModule = `${nodeId}-${nodeConnId}-${normalizedModule}`;
      const keyWithoutModule = `${nodeId}-${nodeConnId}-`;

      if (savedKey === keyWithModule || savedKey === keyWithoutModule) {
        return savedKey;
      }

      // Check if saved key matches pattern with nodeId-connId-
      // If it's a SQL connector and saved key ends with '-', treat it as 'sql'
      if (
        savedKey.endsWith('-') &&
        savedKey.startsWith(`${nodeId}-${nodeConnId}-`)
      ) {
        // For SQL connectors, if saved key has empty module, use 'sql'
        if (isSqlConnector && savedKey === keyWithoutModule) {
          return keyWithModule; // Will have 'sql' as module
        }
        // For other connectors, return key with actual module (may be empty)
        return keyWithModule;
      }
    }

    // If exact match failed, try parsing the key and matching by connId and module
    const parsed = parseKey(savedKey);
    if (parsed) {
      const { connId, module } = parsed;
      
      // Find node by connId and module
      const matchedNode = allNodes.find((node) => {
        const nodeConnId = node?.data?.config?.connection?.conn_id || node?.config?.connection?.conn_id || '';
        const isSqlConnector = node.id?.toLowerCase().startsWith('sql') || 
                              node?.data?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              node?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              node?.data?.config?.connection?.connector?.toLowerCase() === 'sql' ||
                              node?.config?.connection?.connector?.toLowerCase() === 'sql';
        
        const nodeModule = node?.data?.config?.module || node?.config?.module || '';
        
        // Normalize module: for SQL, normalize to lowercase; for others, use as-is
        let normalizedModule = nodeModule;
        if (isSqlConnector) {
          if (!nodeModule) {
            normalizedModule = 'sql'; // Default for SQL when empty
          } else if (nodeModule.toLowerCase() === 'sql') {
            normalizedModule = 'sql'; // Normalize SQL to lowercase
          }
        }
        
        // Match: if saved module is empty and node is SQL, treat as 'sql'
        const savedModuleNormalized = (!module || module === '') && isSqlConnector ? 'sql' : module;
        
        return nodeConnId === connId && (normalizedModule === savedModuleNormalized || (!savedModuleNormalized && !normalizedModule));
      });
      
      if (matchedNode) {
        const nodeId = matchedNode.id;
        const isSqlConnector = nodeId?.toLowerCase().startsWith('sql') || 
                              matchedNode?.data?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              matchedNode?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              matchedNode?.data?.config?.connection?.connector?.toLowerCase() === 'sql' ||
                              matchedNode?.config?.connection?.connector?.toLowerCase() === 'sql';
        
        const nodeModule = matchedNode?.data?.config?.module || matchedNode?.config?.module || '';
        
        // Normalize module: for SQL, normalize to lowercase; for others, use as-is
        let normalizedModule = nodeModule;
        if (isSqlConnector) {
          if (!nodeModule) {
            normalizedModule = 'sql'; // Default for SQL when empty
          } else if (nodeModule.toLowerCase() === 'sql') {
            normalizedModule = 'sql'; // Normalize SQL to lowercase
          }
        }
        
        return `${nodeId}-${connId}-${normalizedModule}`;
      }
    }

    // Fallback: try matching by connId only (if key contains connId)
    for (const node of allNodes) {
      const nodeConnId = node?.data?.config?.connection?.conn_id || node?.config?.connection?.conn_id || '';
      if (!nodeConnId) continue;
      
      // Check if saved key contains the connId
      if (savedKey.includes(nodeConnId)) {
        const nodeId = node.id;
        const isSqlConnector = nodeId?.toLowerCase().startsWith('sql') || 
                              node?.data?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              node?.config?.connection?.connector_name?.toLowerCase() === 'sql' ||
                              node?.data?.config?.connection?.connector?.toLowerCase() === 'sql' ||
                              node?.config?.connection?.connector?.toLowerCase() === 'sql';
        
        const nodeModule = node?.data?.config?.module || node?.config?.module || '';
        
        // Normalize module: for SQL, normalize to lowercase; for others, use as-is
        let normalizedModule = nodeModule;
        if (isSqlConnector) {
          if (!nodeModule) {
            normalizedModule = 'sql'; // Default for SQL when empty
          } else if (nodeModule.toLowerCase() === 'sql') {
            normalizedModule = 'sql'; // Normalize SQL to lowercase
          }
        }
        
        return `${nodeId}-${nodeConnId}-${normalizedModule}`;
      }
    }

    return savedKey;
  }, []);

  useEffect(() => {
    const loadedWorkflow = workflows[workflowId];


    if (loadedWorkflow?.connectionDetails?.length) {
      console.log(
        '🔄 Restoring connectionDetails:',
        loadedWorkflow.connectionDetails.length,
      );
    }





    if (loadedWorkflow?.config?.entitySelections && nodes.length > 0) {
      const { source = {}, destination = {} } =
        loadedWorkflow.config.entitySelections;


      Object.entries(source).forEach(([savedKey, entities]) => {
        const normalizedKey = normalizeEntityKey(savedKey, nodes, 'source');
        const existing = selectedSourceEntitiesByWorkflow[normalizedKey] || [];


        if (!existing || existing.length === 0) {
          console.log(
            `🔄 Normalizing source key: "${savedKey}" -> "${normalizedKey}"`,
          );
          dispatch(
            setConfirmedSourceEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
          dispatch(
            setSelectedSourceEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
        }
      });


      Object.entries(destination).forEach(([savedKey, entities]) => {
        const normalizedKey = normalizeEntityKey(
          savedKey,
          nodes,
          'destination',
        );
        const existing =
          selectedDestinationEntitiesByWorkflow[normalizedKey] || [];


        if (!existing || existing.length === 0) {
          console.log(
            `🔄 Normalizing destination key: "${savedKey}" -> "${normalizedKey}"`,
          );
          dispatch(
            setConfirmedDestinationEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
          dispatch(
            setSelectedDestinationEntities({
              workflowId,
              key: normalizedKey,
              entities,
            }),
          );
        }
      });
    }
  }, [
    workflowId,
    workflows,
    nodes,
    dispatch,
    normalizeEntityKey,
    selectedSourceEntitiesByWorkflow,
    selectedDestinationEntitiesByWorkflow,
  ]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!workflowId || !hasMounted.current) return;

    const timeout = setTimeout(() => {
      const wfId = workflowId;

      dispatch(
        saveWorkflow({
          workflowId: wfId,
          nodes,
          edges,
          connectionDetails,

          ...(workflowConfigurationId && { workflowConfigurationId }),
          ...(workflowRunId && { workflowRunId }),
        }),
      );
    }, 800);

    return () => clearTimeout(timeout);
  }, [
    nodes,
    edges,
    workflowId,
    connectionDetails,
    dispatch,


  ]);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const makeMutableNodes = useCallback(
    (list) => {
      try {
        if (typeof structuredClone === 'function') {
          return structuredClone(list);
        }
      } catch (err) {
        console.warn('structuredClone failed, falling back to JSON clone', err);
      }
      try {
        return JSON.parse(JSON.stringify(list));
      } catch (err) {
        console.warn('JSON clone failed, falling back to shallow copy', err);
      }
      return list.map((n) => ({
        ...n,
        data: n.data ? { ...n.data } : {},
        position: n.position ? { ...n.position } : {},
        style: n.style ? { ...n.style } : undefined,
      }));
    },
    [],
  );

  const onNodesChange = useCallback(
    (changes) => {
      const mutable = makeMutableNodes(nodes);
      dispatch(setNodes(applyNodeChanges(changes, mutable)));
    },
    [nodes, dispatch, makeMutableNodes],
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const thawedEdges = edges.map((e) => ({
        ...e,
        style: e.style ? { ...e.style } : undefined,
      }));
      dispatch(setEdges(applyEdgeChanges(changes, thawedEdges)));
    },
    [edges, dispatch],
  );

  const handleDatasetSave = () => {
    if (!datasetSelection) {
      toast.error('Select a dataset first');
      return;
    }
    
    let target = datasetTarget;
    if (!target && selectedNode) {
      if (selectedNode.data?.isPlaceholder) {
        target = { nodeId: selectedNode.id, nodeType: selectedNode.type };
      } else {
        target = { nodeId: selectedNode.id, nodeType: selectedNode.type };
      }
    }
    
    if (!target) {
      const placeholderNode = nodes.find((n) => n.data?.isPlaceholder);
      if (placeholderNode) {
        target = { nodeId: placeholderNode.id, nodeType: placeholderNode.type };
      }
    }

    const isDestination =
      target?.nodeType === 'destinationNode' || target?.nodeType === 'destination';
    const nodeTypeKey = isDestination ? 'destinationNode' : 'sourceNode';
    const labelSuffix = isDestination ? 'Destination' : 'Source';
    const label = `${datasetSelection.label} (${labelSuffix})`;

    if (target?.nodeId) {
      const oldNode = nodes.find((node) => node.id === target.nodeId);
      const isPlaceholderNode = oldNode?.data?.isPlaceholder || 
                                oldNode?.id?.startsWith('Select Source') || 
                                oldNode?.id?.startsWith('Select Destination');
      
      // If it's a placeholder node, replace it with a new node (to update the ID)
      if (isPlaceholderNode) {
        const newId = `${datasetSelection.label}-${uuidv4()}`;
        const newNode = {
          id: newId,
          type: nodeTypeKey,
          position: oldNode?.position || { x: 0, y: 0 },
          data: {
            label,
            isPlaceholder: false,
            config: {
              label,
              // Don't set module here - it will be set when connection is configured
              // Preserve existing module if it exists
              module: oldNode?.data?.config?.module || undefined,
              connection: {
                ...(oldNode?.data?.config?.connection || {}),
                conn_name: datasetSelection.label,
                connector_name: datasetSelection.label,
              },
            },
          },
        };
        
        // Update edges to reference the new node ID
        const updatedEdges = edges.map((edge) => {
          if (edge.source === target.nodeId) {
            return { ...edge, source: newId };
          }
          if (edge.target === target.nodeId) {
            return { ...edge, target: newId };
          }
          return edge;
        });
        
        // Remove old node and add new node
        const updatedNodes = nodes
          .filter((node) => node.id !== target.nodeId)
          .concat(newNode);
        
        dispatch(setNodes(updatedNodes));
        dispatch(setEdges(updatedEdges));
      } else {
        // For non-placeholder nodes, just update the data
        const updatedNodes = nodes.map((node) => {
          if (node.id !== target.nodeId) return node;
          return {
            ...node,
            type: nodeTypeKey,
            data: {
              ...node.data,
              label,
              isPlaceholder: false,
              placeholderLabel: undefined,
              placeholderHint: undefined,
              placeholderKind: undefined,
              config: {
                ...(node.data?.config || {}),
                label,
                // Preserve existing module - don't override it
                module: node.data?.config?.module,
                connection: {
                  ...(node.data?.config?.connection || {}),
                  conn_name: datasetSelection.label,
                  connector_name: datasetSelection.label,
                },
              },
            },
          };
        });
        dispatch(setNodes(updatedNodes));
      }
    } else {
      const pos = resolveDropPosition();
      const newNode = {
        id: `${datasetSelection.label}-${uuidv4()}`,
        type: nodeTypeKey,
        position: pos,
        data: {
          label,
          isPlaceholder: false,
          config: {
            label,
            // Don't set module here - it will be set when connection is configured
            connection: {
              conn_name: datasetSelection.label,
              connector_name: datasetSelection.label,
            },
          },
        },
      };
      dispatch(setNodes([...nodes, newNode]));
    }

    setSelectSourceModalOpen(false);
    setDatasetSelection(null);
    setDatasetTarget(null);
    setSelectedNode(null);
  };

















































  const onConnect = useCallback(
    (params) => {
      const { source, target } = params;

      const sourceNode = nodes.find((n) => n.id === source);
      const targetNode = nodes.find((n) => n.id === target);

      console.log('CONNECT ATTEMPT', {
        source,
        target,
        sourceType: sourceNode?.type,
        targetType: targetNode?.type,
      });

      const isIntegration = (n) => n?.type === 'integrationNode';
      const isDecisionIntegration = (n) =>
        isIntegration(n) &&
        (/^decision-/i.test(n?.id) || /^decision/i.test(n?.data?.label));

      if (!sourceNode || !targetNode) return;






























      const isTargetIntegration = isIntegration(targetNode);
      const isSourceNode = sourceNode.type === 'sourceNode';
      const isSourceFunctionNode = sourceNode.type === 'functionNode';

      if (isTargetIntegration) {
        const decision = isDecisionIntegration(targetNode);
        const connectedEdges = edges.filter((e) => e.target === target);

        const sourceConnections = connectedEdges.filter((e) => {
          const node = nodes.find((n) => n.id === e.source);
          return node?.type === 'sourceNode' || node?.type === 'functionNode';
        });

        if ((isSourceNode || isSourceFunctionNode) && decision && sourceConnections.length >= 1) {
          toast.error('Decision node accepts only 1 source');
          return;
        }
        if ((isSourceNode || isSourceFunctionNode) && !decision && sourceConnections.length >= 3) {
          toast.error('Max 3 source/function nodes can connect to an integration node');
          return;
        }
      }

      if (targetNode.type === 'destinationNode') {
        const hasInput = edges.some((e) => e.target === target);
        if (hasInput) {
          toast.error('Destination node can only accept 1 connection');
          return;
        }
      }


      if (
        targetNode.type === 'backtrackNode' ||
        sourceNode.type === 'backtrackNode'
      ) {
        const existingBacktrackNodes = nodes.filter(
          (n) => n.type === 'backtrackNode',
        );
        if (
          existingBacktrackNodes.length > 0 &&
          !existingBacktrackNodes.some(
            (n) => n.id === source || n.id === target,
          )
        ) {
          toast.error('Only 1 backtrack node is allowed per workflow');
          return;
        }
      }































      if (sourceNode.type === 'backtrackNode') {

        if (targetNode.type !== 'sourceNode') {
          toast.error('Backtrack node can only connect to source nodes');
          return;
        }

      }

      if (targetNode.type === 'backtrackNode') {

        if (sourceNode.type !== 'destinationNode') {
          toast.error(
            'Backtrack node can only accept connections from destination nodes',
          );
          return;
        }

      }


      const isBacktrackEdge =
        sourceNode.type === 'backtrackNode' ||
        targetNode.type === 'backtrackNode';


      const edgeColor = isBacktrackEdge
        ? '#9c27b6'
        : darkMode
          ? '#3a8eff'
          : '#1976d2';

      const newEdge = {
        ...params,
        animated: true,
        markerEnd: {
          type: 'arrowclosed',
          width: 20,
          height: 20,
          color: edgeColor,
        },
        ...(isBacktrackEdge && {
          style: { stroke: '#9c27b6', strokeWidth: 2 },
        }),
      };
      dispatch(setEdges(addEdge(newEdge, edges)));
    },
    [nodes, edges, dispatch, darkMode],
  );

  const onEdgeClick = useCallback(
    (event, edge) => {
      const updatedEdges = edges.filter((e) => e.id !== edge.id);
      dispatch(setEdges(updatedEdges));
    },
    [edges, dispatch],
  );







  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const multiData = event.dataTransfer.getData('multi-nodes');
      const nodeType = event.dataTransfer.getData('nodeType');
      const type = event.dataTransfer.getData('application/reactflow');

      const dropPosition = reactFlowInstance.current?.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const nodeTypesMap = {
        Source: 'sourceNode',
        Destination: 'destinationNode',
        Transformation: 'transformationNode',
        Processor: 'processorNode',
        Integretion: 'integrationNode',
        Backtrack: 'backtrackNode',
        functionNode: 'functionNode',
      };

      let functionNodeData = null;
      try {
        const parsedType = JSON.parse(type);
        if (parsedType?.type === 'functionNode') {
          functionNodeData = parsedType;
        }
      } catch (e) {
        // eslint-disable-next-line no-empty
      }

      const inferredKind = (() => {
        if (functionNodeData) {
          return 'functionNode';
        }
        if (multiData) {
          try {
            const parsed = JSON.parse(multiData);
            if (parsed?.[0]?.type) return parsed[0].type;
            if (parsed?.[0]?.sourceType) return parsed[0].sourceType;
          } catch (err) {
            console.warn('Failed to parse multiData during drop', err);
          }
        }
        return nodeType || type;
      })();

      if (inferredKind === 'Source' || inferredKind === 'Destination') {
        const label =
          inferredKind === 'Source' ? 'Select Source' : 'Select Destination';
        const hint =
          inferredKind === 'Source'
            ? 'Choose your data source'
            : 'Choose your data destination';
        const nodeTypeKey = nodeTypesMap[inferredKind] || 'sourceNode';

        const newNode = {
          id: `${label}-${uuidv4()}`,
          type: nodeTypeKey,
          position: dropPosition || { x: 0, y: 0 },
          data: {
            label,
            isPlaceholder: true,
            placeholderLabel: label,
            placeholderHint: hint,
            placeholderKind: inferredKind.toLowerCase(),
          },
        };

        dispatch(setNodes([...nodes, newNode]));
        setClearSourceSelection((p) => p + 1);
        return;
      }

      const getConfig = (t) => {
        if (t === 'integrationNode') return { custom: {} };
        if (t === 'backtrackNode') return { update_operation: 'upsert' };
        return '';
      };

      const centerX = reactFlowBounds.width / 2;
      const centerY = reactFlowBounds.height / 3;
      const base = reactFlowInstance.current.screenToFlowPosition({
        x: centerX,
        y: centerY,
      });

      const integrationNodes = nodes.filter(
        (n) => n.type === 'integrationNode',
      );
      const getPos = (n) => ({
        x: n.positionAbsolute?.x ?? n.position?.x ?? 0,
        y: n.positionAbsolute?.y ?? n.position?.y ?? 0,
      });
      let anchor = base;
      if (integrationNodes.length) {
        let best = null,
          bestD2 = Infinity;
        for (const n of integrationNodes) {
          const p = getPos(n);
          const d2 =
            (p.x - base.x) * (p.x - base.x) + (p.y - base.y) * (p.y - base.y);
          if (d2 < bestD2) {
            bestD2 = d2;
            best = p;
          }
        }
        if (best) anchor = best;
      }

      const LANE_GAP = 280;
      const laneX = (t) => {
        if (t === 'sourceNode') return anchor.x - LANE_GAP;
        if (t === 'destinationNode') return anchor.x + LANE_GAP;
        if (t === 'backtrackNode') return anchor.x + LANE_GAP / 2;
        return anchor.x;
      };

      if (multiData) {
        const items = JSON.parse(multiData);
        const newNodes = [];
        const newEdges = [];
        const idMap = { sourceNode: [], destinationNode: [] };
        const rowCounters = {};

        items.forEach((item) => {
          const typeKey = item.type || item.sourceType || 'Source';
          const nodeTypeKey = nodeTypesMap[typeKey] || 'sourceNode';
          const id = `${item.name}-${uuidv4()}`;

          const row = rowCounters[nodeTypeKey] ?? 0;
          rowCounters[nodeTypeKey] = row + 1;

          newNodes.push({
            id,
            type: nodeTypeKey,
            position: { x: laneX(nodeTypeKey), y: anchor.y + row * 150 },
            data: {
              label: `${item.name} (${typeKey})`,
              config: getConfig(nodeTypeKey),
            },
          });

          if (!idMap[nodeTypeKey]) idMap[nodeTypeKey] = [];
          idMap[nodeTypeKey].push(id);
        });

        const isDecisionIntegration = (n) =>
          n.type === 'integrationNode' &&
          (/^decision-/i.test(n.id) || /^decision/i.test(n?.data?.label || ''));

        integrationNodes.forEach((integrationNode) => {
          const integrationId = integrationNode.id;
          const decision = isDecisionIntegration(integrationNode);

          const existingSrc = edges
            .filter((e) => e.target === integrationId)
            .filter(
              (e) =>
                nodes.find((n) => n.id === e.source)?.type === 'sourceNode',
            ).length;

          let addedSrc = 0;
          const addSrc = (srcId) => {
            if (
              edges.some(
                (e) => e.source === srcId && e.target === integrationId,
              )
            )
              return;
            if (
              newEdges.some(
                (e) => e.source === srcId && e.target === integrationId,
              )
            )
              return;
            newEdges.push({
              id: `e-${srcId}-${integrationId}`,
              source: srcId,
              target: integrationId,
              type: 'default',
            });
          };

          if (decision) {
            for (const srcId of idMap.sourceNode) {
              if (existingSrc + addedSrc >= 1) break;
              addSrc(srcId);
              addedSrc += 1;
            }
          } else {
            const CAP = 3;
            for (const srcId of idMap.sourceNode) {
              if (existingSrc + addedSrc >= CAP) break;
              addSrc(srcId);
              addedSrc += 1;
            }
          }

          const existingDest = edges
            .filter((e) => e.source === integrationId)
            .filter((e) => {
              const targetNode = nodes.find((n) => n.id === e.target);
              return (
                targetNode?.type === 'destinationNode' ||
                targetNode?.type === 'destination'
              );
            }).length;

          let addedDest = 0;

          for (const destId of idMap.destinationNode) {
            if (!decision && existingDest + addedDest >= 3) break;
            if (edges.some((e) => e.target === destId)) continue;
            if (newEdges.some((e) => e.target === destId)) continue;
            newEdges.push({
              id: `e-${integrationId}-${destId}`,
              source: integrationId,
              target: destId,
              type: 'default',
            });
            addedDest += 1;
          }
        });

        dispatch(setNodes([...nodes, ...newNodes]));
        dispatch(setEdges([...edges, ...newEdges]));
        setClearSourceSelection((p) => p + 1);
        return;
      }

      if (functionNodeData) {
        const newNode = {
          id: `function_${+new Date()}`,
          type: 'functionNode',
          position: dropPosition || { x: 0, y: 0 },
          draggable: true,
          data: {
            label: functionNodeData.functionType, // FunctionNode component will resolve the label
            functionType: functionNodeData.functionType,
            icon: functionNodeData.icon,
            config: functionNodeData.config || {},
          },
        };

        dispatch(setNodes([...nodes, newNode]));
        return;
      }

      if (!type || !nodeType) return;


      const nodeTypeKey =
        type === 'Backtrack'
          ? nodeTypesMap['Backtrack']
          : nodeTypesMap[nodeType] || 'default';

      if (nodeTypeKey === 'backtrackNode') {
        const hasConnectionJson =
          Array.isArray(connectionDetails) && connectionDetails.length > 0;

        if (!hasConnectionJson) {
          toast.error(
            'Backtrack node can be added only after you configure at least one mapping/connection.',
          );
          return;
        }


        const existingBacktrack = nodes.some((n) => n.type === 'backtrackNode');
        if (existingBacktrack) {
          toast.error('Only 1 backtrack node is allowed per workflow');
          return;
        }
      }
      const offsetY = Math.floor(nodes.length / 5) * 50;

      const newNode = {
        id: `${type}-${uuidv4()}`,
        type: nodeTypeKey,
        position: { x: laneX(nodeTypeKey), y: anchor.y + offsetY },
        data: {
          label: `${type} (${nodeType})`,
          config: getConfig(nodeTypeKey),
        },
      };


      const newEdges = [];
      if (nodeTypeKey === 'backtrackNode') {

        setEnableBacktrack(true);


        const destinationNodes = nodes.filter(
          (n) => n.type === 'destinationNode' || n.type === 'destination',
        );



        const sourceNodes = nodes.filter(
          (n) => n.type === 'sourceNode' || n.type === 'source',
        );


        destinationNodes.forEach((dest) => {
          newEdges.push({
            id: `e-${dest.id}-${newNode.id}`,
            source: dest.id,
            target: newNode.id,
            animated: true,
            markerEnd: {
              type: 'arrowclosed',
              width: 20,
              height: 20,
              color: '#9c27b6',
            },
            style: { stroke: '#9c27b6', strokeWidth: 2 },
          });
        });

        sourceNodes.forEach((src) => {
          newEdges.push({
            id: `e-${newNode.id}-${src.id}`,
            source: newNode.id,
            target: src.id,
            animated: true,
            markerEnd: {
              type: 'arrowclosed',
              width: 20,
              height: 20,
              color: '#9c27b6',
            },
            style: { stroke: '#9c27b6', strokeWidth: 2 },
          });
        });
      }

      dispatch(setNodes([...nodes, newNode]));
      if (newEdges.length > 0) {
        dispatch(setEdges([...edges, ...newEdges]));
      }
      setClearSourceSelection((p) => p + 1);
    },
    [nodes, edges, dispatch, reactFlowInstance, darkMode],
  );

  const handleNodeDoubleClick = (event, node) => {
    setSelectedNode(node);
    // Mark that selection came from canvas (for source/destination nodes)
    if (
      node?.type === 'sourceNode' ||
      node?.type === 'source' ||
      node?.type === 'destinationNode' ||
      node?.type === 'destination'
    ) {
      setNodeSelectedFromCanvas(true);
    }

    if (node.type === 'integrationNode' && /^decision-/i.test(node.id)) {
      setOpenDecisionDrawer(false);
    }
  };

  const DOUBLE_MS = 220;
  const lastClickRef = useRef({ id: null, t: 0 });

  const handleNodeClick = useCallback((e, node) => {
    if (node?.data?.isPlaceholder) {
      setDatasetTarget({ nodeId: node.id, nodeType: node.type });
      setSelectedNode(null);
      setDatasetSelection(null);
      setSelectSourceModalOpen(true);
      return;
    }
    if (
      node?.type === 'sourceNode' ||
      node?.type === 'source' ||
      node?.type === 'destinationNode' ||
      node?.type === 'destination'
    ) {
      setSelectedNode(node);
      setNodeSelectedFromCanvas(true); // Mark that selection came from canvas click
      setDatasetTarget(null);
      setDatasetSelection(null);
      return;
    }
    if (node?.type === 'functionNode') {
      setSelectedNode(node);
      setShowMappingPanel(true);
      if (currentStep === 'selectDataset') {
        setCurrentStep('configureDataset');
      }
      return;
    }
    const now = performance.now();
    const { id, t } = lastClickRef.current;

    if (id === node.id && now - t < DOUBLE_MS) {
      setSelectedNode(node);
      lastClickRef.current = { id: null, t: 0 };
      return;
    }
    lastClickRef.current = { id: node.id, t: now };
  }, [currentStep]);

  const handleSaveConfig = () => {
    setSelectedNode(null);
    setNodeSelectedFromCanvas(false);
  };


























  console.log('node format:', {
    nodes: formattedNodes,
    edges: formattedEdges,
    mappings: connectionDetails,
    entitySelections: sanitizedEntitySelections,
  });
  const handleWorkflowSave = async () => {
    if (!connectionDetails || connectionDetails.length === 0) {
      toast.error('No mapping found! Please configure mapping before saving.', {
        autoClose: 3000,
      });
      return;
    }


    const backtrackNodes = nodes.filter((n) => n.type === 'backtrackNode');
    if (backtrackNodes.length > 1) {
      toast.error('Only 1 backtrack node is allowed per workflow', {
        autoClose: 3000,
      });
      return;
    }


    if (backtrackNodes.length > 0 && !enableBacktrack) {
      setEnableBacktrack(true);
    }

    try {









      dispatch(
        setFinalWorkflowJson({
          workflowId,

          workflowJson: {
            nodes: formattedNodesWithFilter,
            edges: formattedEdges,
            mappings,
            entitySelections: sanitizedEntitySelections,
            filterExpression: filterExpression || null,
            ...(scheduleDateTime && timeZone
              ? {
                  frequency: workflowData?.frequency
                    ? String(workflowData.frequency).toUpperCase()
                    : null,
                  start_date: scheduleDateTime.split('T')[0],
                  end_date: workflowData?.endDate || null,
                  time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
                  timezone: timeZone,
                  ...(String(workflowData?.frequency).toUpperCase() ===
                    'HOURLY' && workflowData?.hourInterval
                    ? { hour_interval: Number(workflowData.hourInterval) }
                    : {}),
                  ...(['WEEKLY', 'MONTHLY'].includes(
                    String(workflowData?.frequency).toUpperCase(),
                  ) &&
                  Array.isArray(workflowData?.customDays) &&
                  workflowData.customDays.length > 0
                    ? { days_of_week: workflowData.customDays }
                    : {}),
                }
              : {}),
          },
        }),
      );

      console.log('nodes of formating new :', {
        nodes: formattedNodes,
        edges: formattedEdges,
        mappings,
        entitySelections: sanitizedEntitySelections,
        run_at: scheduleDateTime,
        timezone: timeZone,
      });


      const allWorkflows = store.getState().workflows.list;
      const matched = allWorkflows.find((w) => w.workflow_id === workflowId);

      if (!matched) {
        toast.error('Workflow metadata not found!');
        return;
      }


      const wfState = store.getState().workflows.workflows?.[workflowId] || {};
      const recurrenceFrequency = wfState.frequency || null;
      const recurrenceEndDate = wfState.endDate || null;
      const recurrenceHourInterval = wfState.hourInterval || null;
      const recurrenceDaysOfWeek = wfState.customDays || null;

      const payload = {
        workflow_id: matched.workflow_id ?? null,
        user_id: matched.user_id,
        project_id: matched.project_id,
        name: matched.name,
        description: matched.description || 'auto-saved workflow',
        environment: matched.environment || 'test',
        is_active: matched.is_active ?? true,
        config: {
          nodes: formattedNodesWithFilter,
          edges: formattedEdges,
          mappings,
          entitySelections: sanitizedEntitySelections,
          ...(backtrackNodes.length > 0 || enableBacktrack
            ? {
                featureFlags: {
                  enableBacktrack: enableBacktrack || backtrackNodes.length > 0,
                },
              }
            : {}),
          ...(scheduleDateTime && timeZone
            ? {
                frequency: recurrenceFrequency
                  ? String(recurrenceFrequency).toUpperCase()
                  : null,
                start_date: scheduleDateTime.split('T')[0],
                end_date: recurrenceEndDate || null,
                time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
                timezone: timeZone,
                ...(String(recurrenceFrequency).toUpperCase() === 'HOURLY' &&
                recurrenceHourInterval
                  ? { hour_interval: Number(recurrenceHourInterval) }
                  : {}),
                ...(['WEEKLY', 'MONTHLY'].includes(
                  String(recurrenceFrequency).toUpperCase(),
                ) &&
                Array.isArray(recurrenceDaysOfWeek) &&
                recurrenceDaysOfWeek.length > 0
                  ? { days_of_week: recurrenceDaysOfWeek }
                  : {}),
              }
            : {}),
        },

      };

      console.log('payload:::', payload);
      console.log('payload.config::', payload.config);
      const response = await postWorkflowToBackend(payload);
      console.log('Workflow saved to backend:', response);

      const savedWorkflowId = response?.workflow_id ?? workflowId;
      const savedWorkflowConfigurationId =
        response?.workflow_configuration_id ?? workflowConfigurationId;





      dispatch(
        saveWorkflow({
          nodes,
          edges,
          connectionDetails,
          workflowId: savedWorkflowId,
          workflowConfigurationId: savedWorkflowConfigurationId,
          ...(workflowRunId && { workflowRunId }),
        }),
      );

      dispatch(clearWorkflowDirty({ workflowId: savedWorkflowId }));
      dirtySnapshotRef.current = dirtySnapshot;
      dirtyInitializedRef.current = true;

      toast.success('Workflow saved successfully!', {
        autoClose: 2000,
      });

      if (scheduleDateTime && timeZone && response.workflow_id) {


        const triggerResponse = await triggerExecuteWorkflow(
          response.workflow_id,
          {
            frequency: recurrenceFrequency
              ? String(recurrenceFrequency).toUpperCase()
              : null,
            start_date: scheduleDateTime.split('T')[0],
            end_date: recurrenceEndDate || null,
            time_of_day: scheduleDateTime.split('T')[1]?.slice(0, 5),
            timezone: timeZone,
            ...(String(recurrenceFrequency).toUpperCase() === 'HOURLY' &&
            recurrenceHourInterval
              ? { hour_interval: Number(recurrenceHourInterval) }
              : {}),
            ...(['WEEKLY', 'MONTHLY'].includes(
              String(recurrenceFrequency).toUpperCase(),
            ) &&
            Array.isArray(recurrenceDaysOfWeek) &&
            recurrenceDaysOfWeek.length > 0
              ? { days_of_week: recurrenceDaysOfWeek }
              : {}),
          },
        );

        dispatch(
          setTriggerResponse({
            workflowId,
            triggerResponse,
          }),
        );
        toast.success('Workflow scheduled', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Failed to save workflow:', error);
      toast.error('Error saving workflow to backend.', {
        autoClose: 3000,
      });
    }
  };


  useImperativeHandle(ref, () => ({
    handleWorkflowSave,
  }));

  console.log(
    'batch delte:',
    nodes.filter((n) => n.selected),
  );

  const selectedNodes = nodes.filter((n) => n.selected);

  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });

  useEffect(() => {
    if (reactFlowInstance.current) {
      const { x, y, zoom } = reactFlowInstance.current.getViewport();
      setViewport({ x, y, zoom });
    }
  }, [nodes, edges]);


  useEffect(() => {
    const handleResize = () => {
      if (reactFlowInstance.current) {

        requestAnimationFrame(() => {
          reactFlowInstance.current.fitView({
            padding: 0.2,
            includeHiddenNodes: false,
            minZoom: 0.3,
            maxZoom: 1.0,
          });
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (reactFlowInstance.current) {

      const timer = setTimeout(() => {
        reactFlowInstance.current.fitView({
          padding: 0.2,
          includeHiddenNodes: false,
          minZoom: 0.3,
          maxZoom: 1.0,
        });
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [selectedNode]);

  function flowToDom({ x, y }) {
    return {
      x: x * viewport.zoom + viewport.x,
      y: y * viewport.zoom + viewport.y,
    };
  }

  const selectionBox = getSelectionBoundingBox(selectedNodes);

  function getSelectionBoundingBox(nodes) {
    if (nodes.length === 0) return null;
    const minX = Math.min(...nodes.map((n) => n.position.x));
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const maxX = Math.max(
      ...nodes.map((n) => n.position.x + (n.measured?.width || 56)),
    );
    const maxY = Math.max(
      ...nodes.map((n) => n.position.y + (n.measured?.height || 56)),
    );
    return { minX, minY, maxX, maxY };
  }

  console.log('selectionBox:::', selectionBox);

























  const handleBatchDelete = () => {

    const selectedIds = selectedNodes.map((n) => n.id);
    const selectedLabels = selectedNodes.map((n) => n.data.label);


    setPendingDeleteNodeIds(selectedIds);
    setPendingDeleteNodeLabel(selectedLabels);


    setConfirmDialogOpen(true);
  };


  const deleteNode = (nodeId) => {
    console.log('ACTUAL NODE DELETE FIRED', nodeId);

    const node = nodes.find((n) => n.id === nodeId);

    const updatedNodes = nodes.filter((node) => node.id !== nodeId);
    dispatch(setNodes(updatedNodes));

    const updatedEdges = edges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId,
    );

    dispatch(setEdges(updatedEdges));

    console.log('delete node data::::::', node);

    if (
      node &&
      (node.type === 'sourceNode' ||
        node.type === 'destinationNode' ||
        node.type === 'integrationNode')
    ) {
      console.log('node config:', node.data);
      const conn_id = node.data?.config?.connection?.conn_id || '';
      const module = node.data?.config?.module || '';
      const key = `${node.id}-${conn_id}-${module}`;
      const key1 = `${node.id}`;


      console.log('delete node data 1::::::', node);

      dispatch(removeEntitiesForKey({ workflowId, key }));
      dispatch(removeFinalConfigById({ workflowId, id: key1 }));
      dispatch(resetMappingForWorkflow(workflowId));
      dispatch(
        setConfirmedSourceEntities({
          workflowId,
          key: key,
          entities: [],
        }),
      );
      dispatch(
        setConfirmedDestinationEntities({
          workflowId,
          key: key,
          entities: [],
        }),
      );
    }
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteNodeId) {

      deleteNode(pendingDeleteNodeId);
    } else if (pendingDeleteNodeIds.length > 0) {

      const updatedNodes = nodes.filter(
        (n) => !pendingDeleteNodeIds.includes(n.id),
      );
      dispatch(setNodes(updatedNodes));

      const updatedEdges = edges.filter(
        (e) =>
          !pendingDeleteNodeIds.includes(e.source) &&
          !pendingDeleteNodeIds.includes(e.target),
      );
      dispatch(setEdges(updatedEdges));

      selectedNodes.forEach((node) => {
        if (
          node.type === 'sourceNode' ||
          node.type === 'destinationNode' ||
          node.type === 'integrationNode'
        ) {
          const conn_id = node.data?.config?.connection?.conn_id || '';
          const module = node.data?.config?.module || '';
          const key = `${node.id}-${conn_id}-${module}`;
          const key1 = `${node.id}`;

          console.log('delete node data 2::::::', node);
          console.log('key1::::::', key1);

          dispatch(removeEntitiesForKey({ workflowId, key }));
          dispatch(removeFinalConfigById({ workflowId, id: key1 }));
          dispatch(
            setConfirmedSourceEntities({
              workflowId,
              key: key,
              entities: [],
            }),
          );
          dispatch(
            setConfirmedDestinationEntities({
              workflowId,
              key: key,
              entities: [],
            }),
          );

          dispatch(resetMappingForWorkflow(workflowId));
        }
      });
    }


    setConfirmDialogOpen(false);
    setPendingDeleteNodeId(null);
    setPendingDeleteNodeLabel('');
    setPendingDeleteNodeIds([]);
  };
  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setPendingDeleteNodeId(null);
    setPendingDeleteNodeLabel('');
    setPendingDeleteNodeIds([]);
  };

  const updateNodeData = (nodeId, newConfig) => {
    const updatedNodes = nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const existingConfig = node.data?.config || {};

      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...existingConfig,
            ...newConfig,
          },
        },
      };
    });

    dispatch(setNodes(updatedNodes));
  };

  const handleRequestSqlConnectionFix = useCallback(
    (payload = {}) => {
      if (!payload?.nodeId) return;
      const targetNode = nodes.find((node) => node.id === payload.nodeId);
      if (!targetNode) return;

      const fallbackHost =
        targetNode.data?.config?.sqlMetadata?.host ||
        targetNode.data?.config?.connection?.sqlMetadata?.host ||
        '';
      const fallbackPort =
        targetNode.data?.config?.sqlMetadata?.port ||
        targetNode.data?.config?.connection?.sqlMetadata?.port ||
        '';

      setSqlHostPortDialog({
        nodeId: targetNode.id,
        connId:
          payload.connId ||
          targetNode.data?.config?.connection?.conn_id ||
          null,
        connectionName:
          payload.connectionName ||
          targetNode.data?.config?.connection?.conn_name ||
          targetNode.data?.label ||
          targetNode.data?.config?.label ||
          'SQL Connection',
        host: payload.host || fallbackHost,
        port: payload.port || fallbackPort,
      });
    },
    [nodes],
  );


  const mergedTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      mode: darkMode ? 'dark' : 'light',
      background: {
        ...theme.palette.background,
        default: darkMode ? '#121212' : '#ffffff',
        paper: darkMode ? '#282828' : '#ffffff',
      },
      text: {
        ...theme.palette.text,
        primary: darkMode ? '#ffffff' : '#000000',
      },
    },
  };

  console.log('nodes in Redux:', nodes);
  console.log('edges in Redux:', edges);

  const destinationOptions = useMemo(() => {
    const nodesArr = Array.isArray(nodes) ? nodes : [];
    const opts = nodesArr
      .filter((n) => n?.type === 'destinationNode')
      .map((n) => {
        const raw =
          n?.data?.label ?? n?.label ?? n?.data?.config?.label ?? n?.id;

        const label = String(raw).replace(/\s*\(.*?\)\s*$/, '') || String(n.id);
        return { value: n.id, label };
      })
      .filter((o) => o.label);


    const seen = new Set();
    return opts.filter((o) =>
      seen.has(o.value) ? false : (seen.add(o.value), true),
    );
  }, [nodes]);

  const workflowDataForMapping = useMemo(() => {
    const formattedNodes = nodes.map((node) => {
      const labelMatch = (node.data?.label || '').match(/(.*?)\s*\((.*?)\)/);
      const label = labelMatch ? labelMatch[1].trim() : (node.data?.label || node.data?.config?.label || '');
      const type = labelMatch ? labelMatch[2].trim().toLowerCase() : node.type;

      return {
        id: node.id,
        position: node.position,
        label,
        type,
        config: node.data?.config || {},
      };
    });

    const formattedEdges = edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      animated: edge.animated,
      label: edge.label || '',
      markerEnd: {
        type: 'arrowclosed',
        width: 20,
        height: 20,
        color: '#1976d2',
      },
    }));

    return { nodes: formattedNodes, edges: formattedEdges };
  }, [nodes, edges]);

  return (
    <ReactFlowProvider>
      <ThemeProvider theme={mergedTheme}>
        <Box
          sx={{
            bgcolor: darkMode ? 'background.default' : '#FAFBFD',
            color: darkMode ? 'text.primary' : 'text.primary',
            minHeight: '100vh',
            width: '100%',
          }}
        >
          <WorkflowActionBar
            onRunNow={() => actionRef.current?.runNow()}
            onScheduleOpen={() => actionRef.current?.openSchedule()}
            workflowId={workflowId}
            setWorkflowName={setWorkflowName}
            onSave={handleWorkflowSave}
            enableBacktrack={enableBacktrack}
            setEnableBacktrack={setEnableBacktrack}
            showExecutionSection={false}
            onToggleExecutionSection={undefined}
          />

          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              bgcolor: darkMode ? '#121212' : undefined,
            }}
          >
            {/* Sidebar */}
            <Sidebar
              ref={sidebarRef}
              workflowId={workflowId}
              setWorkflowName={setWorkflowName}
              onSave={handleWorkflowSave}
              clearSourceSelection={clearSourceSelection}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={setCollapsedBoth}
              activeTab={activeTab}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                height: 'auto',
                minHeight: 'calc(100vh - 100px)',
                overflow: 'visible',
                bgcolor: darkMode ? '#121212' : undefined,
              }}
            >
              <Box 
                sx={{ 
                  px: 2, 
                  pt: 0.5, 
                  pb: 0.5,
                  bgcolor: darkMode ? '#1e1e1e' : '#ffffff',
                  borderBottom: darkMode ? '1px solid #333' : '1px solid #e0e0e0',
                  minHeight: '36px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  width: '100%',
                  flexShrink: 0,
                  position: 'sticky',
                  top: { xs: 84, sm: 88, md: 88, lg: 88, xl: 88 },
                  zIndex: 1201,
                }}
              >
                <Box 
                  sx={{ 
                    mb: 0, 
                    width: '100%',
                    '& > .MuiBox-root': {
                      marginBottom: '0 !important',
                    },
                    '& .MuiTabs-root': {
                      minHeight: '32px',
                    },
                    '& .MuiTabs-flexContainer': {
                      gap: '2px',
                    },
                    '& .MuiTab-root': {
                      minHeight: '32px',
                      height: '32px',
                      padding: '3px 16px',
                      color: darkMode ? '#CCCCCC' : '#6B7280',
                      backgroundColor: 'transparent',
                      border: '1px solid transparent',
                      borderBottom: 'none',
                      marginRight: '0 !important',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        color: darkMode ? '#FFFFFF' : '#1F2937',
                      },
                      '&:not(:first-of-type)': {
                        marginLeft: '2px',
                      },
                    },
                    '& .Mui-selected': {
                      color: darkMode ? '#FFFFFF' : '#1F2937',
                      fontWeight: 600,
                      backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                      border: darkMode ? '1px solid #3e3e42' : '1px solid #e5e7eb',
                      borderBottom: darkMode ? '1px solid #3e3e42' : '1px solid #e5e7eb',
                      '&:hover': {
                        backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                      },
                    },
                  }}
                >
                  <PrimaryTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    tabs={[
                      { label: 'Workflow' },
                      { label: 'Mapping' },
                    ]}
                  />
                </Box>
              </Box>
              {activeTab === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',






                  flexGrow: 1,

                  transition: 'none',
                  width: '100%',






                  maxWidth: '100%',
                  bgcolor: darkMode ? '#121212' : undefined,
                  pt: 2,
                }}
              >
                <Box
                  ref={reactFlowWrapper}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setCollapsedBoth((v) => !v);
                  }}
                  onClick={() => setClearSourceSelection((p) => p + 1)}
                  sx={{
                    flexGrow: 1,
                width:
                  selectedNode && selectedNode.type !== 'integrationNode'
                    ? '98%'
                    : '98%',
                background: darkMode ? '#1a1a1a' : '#E8EAED',
                    position: 'relative',
                    border: `2px dashed ${darkMode ? '#555' : '#555'}`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    m: 1,
                    transition: 'width 0.3s ease-in-out',
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                >
                  <ReactFlow
                    onMove={(_, viewport) => setViewport(viewport)}
                    edgeTypes={edgeTypes}
                    nodes={nodes}
                    onNodesChange={onNodesChange}
                    edges={edges}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onEdgeClick={onEdgeClick}
                    nodeTypes={nodeTypes}
                    onInit={(instance) => {
                      reactFlowInstance.current = instance;

                    }}
                  onNodeDoubleClick={handleNodeDoubleClick}
                  onNodeClick={handleNodeClick}
                  fitView
                    defaultEdgeOptions={defaultEdgeOptions}
                    edgesOptions={{
                      animated: false,
                      style: { stroke: '#3c9362', strokeWidth: 2 },
                    }}
                    maxZoom={0.8}
                    minZoom={0.6}
                    defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
                  >
                    <Background
                      color={darkMode ? '#444' : ' #c8c8c8'}
                      gap={54}
                      size={3}
                      variant={darkMode ? 'dots' : 'dots'}
                    />
                    <Controls
                      style={{
                        background: darkMode ? '#282828' : '#fff',
                        borderColor: darkMode ? '#555' : '#ccc',
                        color: darkMode ? '#fff' : '#000',
                      }}
                    />
                    {!hasSourceNode && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                          zIndex: 5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.75,
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                          }}
                          onClick={handleOpenSelectSourceModal}
                        >
                          <Box
                            sx={{
                              width: 54,
                              height: 54,
                              borderRadius: '3px',
                              backgroundColor: '#ffffff',
                              boxShadow: '0px 0px 4px 0px #00000040',
                              border: '1px solid #E6E6E6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '3px',
                                backgroundColor: '#3C9362',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              >
                              <AddIcon sx={{ color: '#fff', fontSize: 24 }} />
                            </Box>
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: 'IBM Plex Sans, sans-serif',
                              fontWeight: 500,
                              fontStyle: 'normal',
                              fontSize: '0.875rem',
                              lineHeight: '0.86125rem',
                              letterSpacing: '-0.18px',
                              color: '#0F172B',
                              textAlign: 'center',
                              mt: 1,
                              mb: 0.5,
                            }}
                          >
                            Select Source
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 500,
                              fontStyle: 'normal',
                              fontSize: '0.75rem',
                              lineHeight: '0.71875rem',
                              letterSpacing: '-0.09px',
                              textAlign: 'center',
                              color: '#62748E',
                            }}
                          >
                            Choose your data source
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </ReactFlow>

                  {selectionBox &&
                    selectedNodes.length > 1 &&
                    (() => {
                      const center = {
                        x: (selectionBox.minX + selectionBox.maxX) / 2,
                        y: selectionBox.minY + 80,
                      };

                      const domPos = flowToDom(center);

                      return (
                        <IconButton
                          onClick={handleBatchDelete}
                          sx={{
                            position: 'absolute',
                            left: domPos.x - 18,
                            top: domPos.y - 38,
                            zIndex: 1000,
                            bgcolor: 'white',
                            border: '1.5px solid #d32f2f',
                            color: '#d32f2f',
                            boxShadow: 2,
                            '&:hover': { bgcolor: '#d32f2f', color: 'white' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      );
                    })()}
                </Box>
              </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    maxWidth: '100%',
                    bgcolor: darkMode ? '#121212' : undefined,
                    pt: 2,
                    px: 2,
                    height: 'calc(100vh - 140px)',
                  }}
                >
                  <ETLMapping
                    updatedConfig={workflowDataForMapping}
                  />
                </Box>
              )}

              {selectedNode &&
              (selectedNode.type === 'sourceNode' ||
                selectedNode.type === 'source' ||
                selectedNode.type === 'destinationNode' ||
                selectedNode.type === 'destination') &&
              nodeSelectedFromCanvas &&
              currentStep !== 'configureDataset' ? (
                <Dialog
                  open={!!selectedNode}
                  onClose={() => {
                    setSelectedNode(null);
                    setNodeSelectedFromCanvas(false);
                  }}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                    },
                  }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={() => {
                      setSelectedNode(null);
                      setNodeSelectedFromCanvas(false);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: '#666',
                      '&:hover': { color: '#000' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  <DialogContent sx={{ p: 0 }}>
                    <NodeConfigurationPanel
                      selectedNode={selectedNode}
                      setSelectedNode={setSelectedNode}
                      handleSaveConfig={handleSaveConfig}
                      updateNodeData={updateNodeData}
                      onConfigUpdate={(config) => {
                        if (selectedNode) {
                          updateNodeData(selectedNode.id, config);
                        }
                      }}
                    />
                  </DialogContent>
                </Dialog>
              ) : null}

              <SqlHostPortDialog
                open={Boolean(sqlHostPortDialog)}
                connectionName={sqlHostPortDialog?.connectionName}
                initialHost={sqlHostPortDialog?.host}
                initialPort={sqlHostPortDialog?.port}
                connId={sqlHostPortDialog?.connId}
                onClose={() => setSqlHostPortDialog(null)}
                onUpdated={({ host, port }) => {
                  if (!sqlHostPortDialog?.nodeId) {
                    setSqlHostPortDialog(null);
                    return;
                  }
                  const targetNode = nodes.find(
                    (node) => node.id === sqlHostPortDialog.nodeId,
                  );
                  updateNodeData(sqlHostPortDialog.nodeId, {
                    sqlMetadata: {
                      ...(targetNode?.data?.config?.sqlMetadata || {}),
                      host,
                      port,
                    },
                  });
                  setSqlHostPortDialog(null);
                }}
              />

              {selectedNode && selectedNode.type === 'backtrackNode' ? (
                <Dialog
                  open={!!selectedNode}
                  onClose={() => setSelectedNode(null)}
                  maxWidth="sm"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative',
                    },
                  }}
                >
                  <IconButton
                    aria-label="close"
                    onClick={() => setSelectedNode(null)}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: '#666',
                      '&:hover': { color: '#000' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>

                  <DialogContent sx={{ p: 0 }}>
                    <BacktrackConfigurationModal
                      selectedNode={selectedNode}
                      setSelectedNode={setSelectedNode}
                      handleSaveConfig={handleSaveConfig}
                      updateNodeData={updateNodeData}
                      updatedConfig={saveJson}
                      workflowId={workflowId}
                      nodes={nodes}
                      edges={edges}
                    />
                  </DialogContent>
                </Dialog>
              ) : null}

              <InfoDialog
                open={precheckOpen}
                title="Before you continue"
                content={`Define at least ${requiredEntityCount} ${
                  requiredEntityCount === 1 ? 'condition' : 'conditions'
                }, ensuring one for each selected source entity.`}
                onOk={() => {
                  setPrecheckOpen(false);
                  setOpenDecisionDrawer(true);
                }}
                sx={{ zIndex: (t) => t.zIndex.modal + 30 }}
              />

              {selectedNode &&
              selectedNode?.type === 'integrationNode' &&
              selectedNode?.id?.startsWith('decision-') &&
              openDecisionDrawer ? (
                <Box
                  sx={{
                    minWidth: '50px',
                    background: darkMode ? '#282828' : 'white',
                    overflowY: 'auto',
                    height: '100%',
                    transition: 'width 0.3s ease-in-out',
                    color: darkMode ? 'white' : 'inherit',
                  }}
                >
                  <DecisionConfigurationModal
                    selectedNode={selectedNode}
                    setSelectedNode={setSelectedNode}
                    handleSaveConfig={handleSaveConfig}
                    updateNodeData={updateNodeData}
                    sourceResolved={decisionFields}
                    destinationOptions={destinationOptions}
                    onSaveAndNext={() => {
                      const info = getMappingEndpoints(
                        selectedNode,
                        nodes,
                        edges,
                      );
                      setMappingDialogData(info);
                      setMappingDialogOpen(true);
                    }}
                    connectedDestinations={getMappingEndpoints(
                      selectedNode,
                      nodes,
                      edges,
                    )}
                    onClose={() => setOpenDecisionDrawer(false)}
                  />
                </Box>
              ) : null}

              {selectedNode?.type === 'integrationNode' &&

                !openDecisionDrawer &&
                (() => {
                  const integrationNodeId = selectedNode.id;






































                  const sources = nodes.filter((n) => n.type === 'sourceNode');
                  const dests = nodes.filter(
                    (n) => n.type === 'destinationNode',
                  );

                  console.log('sql: sources', sources);
                  console.log('sql: dests', dests);

                  const hasAny = sources.length > 0 && dests.length > 0;










                  const isSqlLike = (n) => {
                    const lbl = (n?.data?.label || '').toLowerCase();
                    return lbl.includes('sql') || lbl.includes('postgres');
                  };

                  const allSrcConfigured = sources.every((sn) => {
                    const cfg = sn.data?.config || {};

                    if (!cfg.connection) return false;

                    if (isSqlLike(sn)) {
                      return true;
                    }

                    return !!cfg.module;
                  });

                  const allDestConfigured = dests.every((dn) => {
                    const cfg = dn.data?.config || {};

                    if (!cfg.connection) return false;

                    if (isSqlLike(dn)) {
                      return true;
                    }

                    return !!cfg.module;
                  });

                  const allSrcEdges = sources.every((sn) =>
                    edges.some(
                      (e) =>
                        e.source === sn.id && e.target === integrationNodeId,
                    ),
                  );
                  const allDestEdges = dests.every((dn) =>
                    edges.some(
                      (e) =>
                        e.source === integrationNodeId && e.target === dn.id,
                    ),
                  );

                  const canOpenDialog =
                    hasAny &&
                    allSrcConfigured &&
                    allDestConfigured &&
                    allSrcEdges &&
                    allDestEdges;

                  return canOpenDialog ? (
                    <EntitySelectionDialog
                      requireEntityChangeForNext={false}

                      open={!precheckOpen && !openDecisionDrawer}
                      selectedNode={selectedNode}
                      onClose={() => setSelectedNode(null)}
                      onNext={(fields) => {
                        setDecisionFields(Array.isArray(fields) ? fields : []);

                        console.log('click');
                        const uniq = new Set(
                          (fields || []).map((f) => String(f.entity)),
                        ).size;
                        setRequiredEntityCount(uniq || 1);
                        setPrecheckOpen(true);
                        console.log('click 2');
                      }}
                      onEntitySelectionComplete={handleEntitySelectionComplete}
                      updatedConfig={processWorkflowDatas}
                      hideAutoFuzzy={
                        (selectedNode?.type === 'integrationNode' ||
                          selectedNode?.type === 'integration') &&
                        /^decision-/i.test(selectedNode?.id)
                      }
                      onRequestSqlConnectionFix={handleRequestSqlConnectionFix}
                    />
                  ) : null;
                })()}
            </Box>

            <RightSideDrawer
              ref={rightSidebarRef}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={setCollapsedBoth}
            />
          </Box>

          {activeTab === 0 && (
            <BottomConfigurationPanel
              currentStep={currentStep}
              onStepChange={handleStepChange}
              mappingData={mappingData}
              resolvedFields={resolvedFields}
              autoMappedFields={autoMappedFields}
              selectedNode={selectedNode}
              showMappingPanel={showMappingPanel || (nodes.length > 0 && edges.length > 0)}
              nodes={nodes}
              workflowId={workflowId}
              onEntitySelectionComplete={handleEntitySelectionComplete}
              onSwitchToVisualBuilder={() => handleTabChange(1)}
              actionRef={actionRef}
              workflowConfigurationId={workflowConfigurationId}
              onWorkflowExecutionComplete={handleWorkflowSave}
              onSave={handleWorkflowSave}
              collapsedBoth={collapsedBoth}
              onCollapsedChange={setCollapsedBoth}
              leftSidebarRef={sidebarRef.current?.isLeftCollapsed}
              rightSidebarRef={rightSidebarRef.current?.isRightCollapsed}
              updateNodeData={updateNodeData}
              onNodeSelect={(node) => {
                setSelectedNode(node);
                setNodeSelectedFromCanvas(false); // Mark that selection came from bottom panel
              }}
            />
          )}
        </Box>
        <Dialog
          open={selectSourceModalOpen}
          onClose={() => setSelectSourceModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '8px',
              overflow: 'hidden',
            },
          }}
        >
          <DialogContent
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 380,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 500,
                    fontSize: '1.25rem',
                    lineHeight: '1.875rem',
                    letterSpacing: '-0.45px',
                    color: '#0A0A0A',
                    mb: 0.5,
                  }}
                >
                  Select Dataset
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    letterSpacing: '-0.15px',
                    color: '#62748E',
                  }}
                >
                  Choose where data comes from
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectSourceModalOpen(false)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <TextField
              placeholder="Search Dataset.."
              fullWidth
              size="small"
              sx={{
                mb: 3,
                  '& .MuiOutlinedInput-root': {
                    background: '#F3F3F5',
                    borderRadius: '8px',
                    '& fieldset': { border: '1px solid #E6E6E6' },
                    '& input': {
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 400,
                      fontSize: '0.875rem',
                      lineHeight: 1.2,
                      letterSpacing: '-0.15px',
                      color: '#717182',
                    },
                  },
              }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3, maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
              {Object.entries(categorizedConnectors).map(([key, category]) => (
                <Box key={key}>
                  <Typography
                    sx={{
                      fontFamily: 'IBM Plex Sans, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      letterSpacing: '-0.15px',
                      color: '#3F3F3F',
                      mb: 1.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    {category.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {category.items.map((item) => (
                      <Box
                        key={item.label}
                        onClick={() => setDatasetSelection(item)}
                        sx={{
                          border: datasetSelection?.label === item.label 
                            ? '2px solid #3C9564' 
                            : '1px solid #E6E6E6',
                          borderRadius: '4px',
                          px: 2,
                          py: 1.5,
                          bgcolor: datasetSelection?.label === item.label 
                            ? '#ECFBF6' 
                            : '#fff',
                          minWidth: 140,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#3C9564',
                            bgcolor: '#ECFBF6',
                            transform: 'translateY(-1px)',
                            boxShadow: '0px 2px 4px rgba(60, 149, 100, 0.15)',
                          },
                        }}
                      >
                        {item.icon ? (
                          <Box
                            component="img"
                            src={item.icon}
                            alt={item.label}
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '4px',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '4px',
                              backgroundColor: '#F3F3F5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 14,
                              color: '#4A4A4A',
                              textTransform: 'uppercase',
                            }}
                          >
                            {item.label.slice(0, 2)}
                          </Box>
                        )}
                        <Typography
                          sx={{
                            fontFamily: 'IBM Plex Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            lineHeight: '24px',
                            letterSpacing: '-0.31px',
                            color: '#0A0A0A',
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 1.5 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#0F172A',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  minHeight: 40,
                  borderRadius: '4px',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  letterSpacing: '0%',
                  textAlign: 'right',
                  '&:hover': { backgroundColor: '#0D1323' },
                }}
                >
                  Create New
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                  variant="outlined"
                  onClick={() => setSelectSourceModalOpen(false)}
                sx={{
                  textTransform: 'none',
                  borderColor: '#DDDFE3',
                  color: '#525252',
                  borderRadius: '4px',
                  borderWidth: '0.83px',
                  minHeight: 40,
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  letterSpacing: '0.33px',
                    textAlign: 'center',
                    verticalAlign: 'middle',
                  }}
                  >
                    Cancel
                  </Button>
                  <Button
                  variant="contained"
                  onClick={handleDatasetSave}
                  disabled={!datasetSelection}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: datasetSelection ? '#3C9564' : '#9EA3AD',
                    color: '#FFFFFF',
                    borderRadius: '4px',
                  minHeight: 40,
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  letterSpacing: '0%',
                    textAlign: 'right',
                    '&:hover': { 
                      backgroundColor: datasetSelection ? '#2E7D32' : '#8b9099',
                    },
                    '&:disabled': {
                      backgroundColor: '#9EA3AD',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Confirm Node Deletion"
          content={`Are you sure you want to remove the node "${pendingDeleteNodeLabel}"?`}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />

        <ProceedMappingDialog
          open={mappingDialogOpen}
          onClose={handleCloseMapping}
          onProceed={(idx) => handleProceedAndNavigate(idx)}
          mappings={(mappingDialogData?.destIds || []).map((_, i) => ({
            sourceLabel: mappingDialogData?.source || 'Source',
            destinationLabel:
              mappingDialogData?.destNames?.[i] || `Mapping ${i + 1}`,
          }))}
          activeIndex={Math.max(
            0,
            (mappingDialogData?.destIds || []).indexOf(
              mappingDialogData?.dest1Id,
            ),
          )}
        />
      </ThemeProvider>
    </ReactFlowProvider>
  );
});

ETLWorkflow.displayName = 'ETLWorkflow';
export default ETLWorkflow;
